// Ledger-domain schemas, types, and helpers. Extracted from api.ts as part of a future-staged refactor.
import { z } from 'zod';
import { BooleanQueryParamSchema, CalendarDateOnlySchema, CalendarDateSchema, LowercaseUuidSchema, PaginationQuerySchema, saVatNumberSchema, writeWarningsFields, } from './api/common.js';
import { VatFilingCategorySchema, VatTreatmentSchema } from './vat.js';
import { LedgerIntentSchema } from './ledger-intent.js';
// Shared YYYY-MM-DD calendar validator for ledger date fields. Aliasing the
// common schema here keeps ledger-specific schemas on the same year-zero and
// impossible-date rules as the rest of the API boundary.
const LedgerDateOnlySchema = CalendarDateOnlySchema;
/** Path params for every ledger-scoped route (`/accounts/:accountId/ledgers/:ledgerId/...`); routes with further path segments `.extend()` it. */
export const LedgerParamsSchema = z.object({
    accountId: z.string().uuid(),
    ledgerId: z.string().uuid(),
});
// =============================================================================
// Ledger account classification enums
// Declared early because ProvisionedAccountLedgerAccountSchema and
// the full LedgerAccount section both reference these schemas.
// =============================================================================
export const LedgerAccountTypeSchema = z.enum([
    'ASSET',
    'LIABILITY',
    'EQUITY',
    'INCOME',
    'EXPENSE',
]);
/**
 * Fine-grained balance-sheet / income-statement classification that replaces
 * the 5-value `LedgerAccountType` on `LedgerAccount` rows. `LedgerAccountType`
 * is retained only for income-statement grouping (via `sectionToType` /
 * `typeToSections`); code allocation now keys on `LedgerAccountCodeBand`
 * (`CODE_BAND_RANGES`, mapped from a section via `sectionToBand`).
 */
export const LedgerAccountSectionSchema = z.enum([
    'CURRENT_ASSETS',
    'NON_CURRENT_ASSETS',
    'CURRENT_LIABILITIES',
    'NON_CURRENT_LIABILITIES',
    'EQUITY',
    'REVENUE',
    'FINANCE_INCOME',
    'OTHER_INCOME',
    'COST_OF_SALES',
    'OPERATING_EXPENSES',
    'FINANCE_COSTS',
    'TAXATION',
    'OTHER_EXPENSES',
]);
/**
 * Frozen source of truth mapping each `LedgerAccountType` to its member
 * `LedgerAccountSection` values. `sectionToType` and `typeToSections` are
 * derived from this map so there is a single place to update if a new section
 * is added.
 */
const SECTIONS_BY_TYPE = Object.freeze({
    ASSET: Object.freeze(['CURRENT_ASSETS', 'NON_CURRENT_ASSETS']),
    LIABILITY: Object.freeze(['CURRENT_LIABILITIES', 'NON_CURRENT_LIABILITIES']),
    EQUITY: Object.freeze(['EQUITY']),
    INCOME: Object.freeze(['REVENUE', 'FINANCE_INCOME', 'OTHER_INCOME']),
    EXPENSE: Object.freeze(['COST_OF_SALES', 'OPERATING_EXPENSES', 'FINANCE_COSTS', 'TAXATION', 'OTHER_EXPENSES']),
});
const SECTION_TO_TYPE = Object.freeze(Object.entries(SECTIONS_BY_TYPE).reduce((acc, [type, sections]) => {
    for (const section of sections) {
        acc[section] = type;
    }
    return acc;
}, {}));
/**
 * Map a `LedgerAccountSection` to its parent `LedgerAccountType`.
 * Derived from `SECTIONS_BY_TYPE`; no separate lookup table needed.
 */
export function sectionToType(section) {
    return SECTION_TO_TYPE[section];
}
/**
 * Return the set of `LedgerAccountSection` values that belong to a given
 * `LedgerAccountType`. Derived from `SECTIONS_BY_TYPE`.
 */
export function typeToSections(type) {
    return SECTIONS_BY_TYPE[type];
}
/**
 * Whether `section` is a NOMINAL section - one whose balances fold into profit
 * at year end (`INCOME` or `EXPENSE`) rather than carrying forward on the
 * balance sheet.
 *
 * The distinction is load-bearing well beyond presentation: the general ledger
 * scopes a nominal account's journal to one financial year, the balance sheet
 * folds nominal balances into retained earnings, and the indirect cash flow
 * statement's movement pass reads balance-sheet movements only.
 */
export function isNominalSection(section) {
    const type = sectionToType(section);
    return type === 'INCOME' || type === 'EXPENSE';
}
// =============================================================================
// Ledger account code bands
// Nine granular bands replace the five-value LedgerAccountType for code
// allocation. Each band owns a distinct 1000-code range, so co-banded
// sections (e.g. CURRENT_ASSETS + NON_CURRENT_ASSETS → ASSETS) share a
// counter while each expense section gets its own counter.
// =============================================================================
export const LedgerAccountCodeBandSchema = z.enum([
    'ASSETS',
    'LIABILITIES',
    'EQUITY',
    'INCOME',
    'COST_OF_SALES',
    'OPERATING_EXPENSES',
    'FINANCE_COSTS',
    'OTHER_EXPENSES',
    'TAXATION',
]);
/**
 * Numeric code ranges for each code band. The first account allocated to a
 * band receives the `start` code; subsequent accounts increment from there.
 *
 * Shared between the backend service (code allocation) and the frontend
 * AccountFormDialog (code-range validation). Single source of truth here
 * avoids the weakly-typed `Record<string, number>` shadow in the UI.
 */
export const CODE_BAND_RANGES = Object.freeze({
    ASSETS: { start: 1000, end: 1999 },
    LIABILITIES: { start: 2000, end: 2999 },
    EQUITY: { start: 3000, end: 3999 },
    INCOME: { start: 4000, end: 4999 },
    COST_OF_SALES: { start: 5000, end: 5999 },
    OPERATING_EXPENSES: { start: 6000, end: 6999 },
    FINANCE_COSTS: { start: 7000, end: 7999 },
    OTHER_EXPENSES: { start: 8000, end: 8999 },
    TAXATION: { start: 9000, end: 9999 },
});
/**
 * Frozen lookup table mapping each `LedgerAccountSection` to its
 * `LedgerAccountCodeBand`. Derived from the mapping below; not exported
 * directly. Use `sectionToBand` instead.
 */
const SECTIONS_BY_BAND = Object.freeze({
    ASSETS: Object.freeze(['CURRENT_ASSETS', 'NON_CURRENT_ASSETS']),
    LIABILITIES: Object.freeze(['CURRENT_LIABILITIES', 'NON_CURRENT_LIABILITIES']),
    EQUITY: Object.freeze(['EQUITY']),
    INCOME: Object.freeze(['REVENUE', 'FINANCE_INCOME', 'OTHER_INCOME']),
    COST_OF_SALES: Object.freeze(['COST_OF_SALES']),
    OPERATING_EXPENSES: Object.freeze(['OPERATING_EXPENSES']),
    FINANCE_COSTS: Object.freeze(['FINANCE_COSTS']),
    OTHER_EXPENSES: Object.freeze(['OTHER_EXPENSES']),
    TAXATION: Object.freeze(['TAXATION']),
});
const SECTION_TO_BAND = Object.freeze(Object.entries(SECTIONS_BY_BAND).reduce((acc, [band, sections]) => {
    for (const section of sections) {
        acc[section] = band;
    }
    return acc;
}, {}));
/**
 * Map a `LedgerAccountSection` to its `LedgerAccountCodeBand`.
 * Derived from `SECTIONS_BY_BAND`; no separate lookup table needed.
 */
export function sectionToBand(section) {
    return SECTION_TO_BAND[section];
}
/**
 * Human-readable labels for each `LedgerAccountSection`.
 *
 * Single source of truth shared between the Bill UI (AccountsTab, AccountFormDialog)
 * and the admin provision-account-detail page. The frontend components that previously
 * defined their own `GROUP_LABELS`, `SECTION_LABELS`, or `LEDGER_SECTION_LABELS` maps
 * should import this instead.
 */
export const LEDGER_SECTION_LABELS = Object.freeze({
    CURRENT_ASSETS: 'Current assets',
    NON_CURRENT_ASSETS: 'Non-current assets',
    CURRENT_LIABILITIES: 'Current liabilities',
    NON_CURRENT_LIABILITIES: 'Non-current liabilities',
    EQUITY: 'Equity',
    REVENUE: 'Revenue',
    FINANCE_INCOME: 'Finance income',
    OTHER_INCOME: 'Other income',
    COST_OF_SALES: 'Cost of sales',
    OPERATING_EXPENSES: 'Operating expenses',
    FINANCE_COSTS: 'Finance costs',
    TAXATION: 'Taxation',
    OTHER_EXPENSES: 'Other expenses',
});
/**
 * Canonical display order for `LedgerAccountSection` values, matching the
 * standard balance-sheet / income-statement sequence.
 *
 * Shared between the Bill UI and the admin dashboard to guarantee a consistent
 * section grouping order across all chart-of-accounts views.
 */
export const LEDGER_SECTION_ORDER = Object.freeze([
    'CURRENT_ASSETS',
    'NON_CURRENT_ASSETS',
    'CURRENT_LIABILITIES',
    'NON_CURRENT_LIABILITIES',
    'EQUITY',
    'REVENUE',
    'FINANCE_INCOME',
    'OTHER_INCOME',
    'COST_OF_SALES',
    'OPERATING_EXPENSES',
    'FINANCE_COSTS',
    'TAXATION',
    'OTHER_EXPENSES',
]);
/**
 * The income statement's sections, in `LEDGER_SECTION_ORDER` sequence - every
 * NOMINAL section and nothing else.
 *
 * Derived BY PREDICATE, never by position. A positional spelling
 * (`LEDGER_SECTION_ORDER.slice(-8)`) is byte-identical today and silently
 * wrong tomorrow: a balance-sheet section appended to the order would join
 * the income statement's reads with no test failing. The one consumer that
 * cannot import this - `ledgerFindMonthlyIncomeStatementAccountTotals.sql`,
 * because TypedSQL cannot reference a TS constant - is pinned against it by
 * `ledger.repository.monthly-income-statement.test.ts` instead.
 */
export const INCOME_STATEMENT_SECTIONS = Object.freeze(LEDGER_SECTION_ORDER.filter(isNominalSection));
export const INCOME_STATEMENT_LAYOUT = Object.freeze([
    { kind: 'section', key: 'REVENUE' },
    { kind: 'section', key: 'COST_OF_SALES' },
    {
        kind: 'subtotal',
        label: 'Gross profit',
        total: 'grossProfitMinorUnits',
        strong: false,
    },
    { kind: 'section', key: 'OTHER_INCOME' },
    { kind: 'section', key: 'OPERATING_EXPENSES' },
    { kind: 'section', key: 'OTHER_EXPENSES' },
    {
        kind: 'subtotal',
        label: 'Operating profit',
        total: 'operatingProfitMinorUnits',
        strong: false,
    },
    { kind: 'section', key: 'FINANCE_INCOME' },
    { kind: 'section', key: 'FINANCE_COSTS' },
    {
        kind: 'subtotal',
        label: 'Profit before tax',
        total: 'profitBeforeTaxMinorUnits',
        strong: false,
    },
    { kind: 'section', key: 'TAXATION' },
    {
        kind: 'subtotal',
        label: 'Net income',
        total: 'netIncomeMinorUnits',
        strong: true,
    },
]);
const BALANCE_SHEET_BODY_SECTION_KEYS = LEDGER_SECTION_ORDER.filter((section) => sectionToType(section) === 'ASSET' || sectionToType(section) === 'LIABILITY');
export const BALANCE_SHEET_LAYOUT = Object.freeze({
    body: Object.freeze([
        ...BALANCE_SHEET_BODY_SECTION_KEYS.map((key) => Object.freeze({ kind: 'section', key })),
        Object.freeze({
            kind: 'equity',
            key: 'EQUITY',
            label: LEDGER_SECTION_LABELS.EQUITY,
            derived: Object.freeze([
                { label: 'Accumulated profit', labelFrom: 'accumulatedProfitAccountName', field: 'accumulatedProfitMinorUnits' },
                { label: 'Current year earnings', field: 'currentYearEarningsMinorUnits' },
            ]),
            subtotal: Object.freeze({ label: 'Total equity', field: 'totalEquityMinorUnits' }),
        }),
    ]),
    footer: Object.freeze([
        Object.freeze({
            label: 'Total assets',
            fields: Object.freeze(['totalAssetsMinorUnits']),
            sections: Object.freeze(LEDGER_SECTION_ORDER.filter((section) => sectionToType(section) === 'ASSET')),
        }),
        Object.freeze({
            label: 'Total liabilities and equity',
            fields: Object.freeze(['totalLiabilitiesMinorUnits', 'totalEquityMinorUnits']),
            sections: Object.freeze([
                ...LEDGER_SECTION_ORDER.filter((section) => sectionToType(section) === 'LIABILITY'),
                'EQUITY',
            ]),
        }),
    ]),
});
/**
 * The sections a balance sheet presents, in presentation order: every body
 * slot's key, equity included.
 *
 * The assembler filters its sections on this, and the projections find theirs
 * through the layout, so "which sections are balance-sheet sections" is stated
 * once rather than re-derived from `sectionToType` at each site.
 */
export const BALANCE_SHEET_SECTIONS = Object.freeze(BALANCE_SHEET_LAYOUT.body.map((slot) => slot.key));
// =============================================================================
// Cash flow classification: treatment (per account) and activity (per report)
// Form-3 presentational data: travels with the section enum beside
// LEDGER_SECTION_ORDER because the section derives the DEFAULT treatment for
// every account. It is only a default. The current/non-current split is a
// maturity classification and the cash flow split is a presentation one, and
// they are orthogonal - so an account may also carry a recorded
// `cashFlowTreatment` that overrides the derivation. Resolve the two through
// `resolveCashFlowTreatment` (or `resolveCashFlowActivity` for the narrowed
// three-way grouping), never by reading `account.cashFlowTreatment` or
// `SECTION_TO_CASH_FLOW_TREATMENT` on its own - each ignores half the answer.
//
// Two enums, deliberately. `CashFlowTreatment` is what an account carries and
// what the indirect statement's operating section buckets on; `CashFlowActivity`
// is the report's three-way grouping, which is all the direct method has.
// `CASH_FLOW_TREATMENT_TO_ACTIVITY` is the only bridge between them.
// =============================================================================
/**
 * IFRS cash flow statement activity classification.
 * Direct method (IFRS for SMEs §7.7(b), §7.9(a)): cash movements are grouped
 * into operating, investing, and financing activities.
 */
export const CashFlowActivitySchema = z.enum(['OPERATING', 'INVESTING', 'FINANCING']);
/**
 * Maps every `LedgerAccountSection` to its IFRS cash flow activity.
 *
 * Policy calls:
 *   - NON_CURRENT_ASSETS -> INVESTING (§7.5: acquisition/disposal of long-term assets)
 *   - NON_CURRENT_LIABILITIES, EQUITY -> FINANCING (§7.6: debt and equity transactions)
 *   - FINANCE_INCOME, FINANCE_COSTS -> OPERATING (§7.15 default, interest/dividends through P&L)
 *   - TAXATION -> OPERATING (§7.17: taxes on income)
 *   - All remaining sections -> OPERATING
 *
 * Kept as a hand-written literal rather than derived from
 * `SECTION_TO_CASH_FLOW_TREATMENT`, so that narrowing the treatment map and
 * comparing it against this one is a real assertion rather than a tautology.
 * `ledger.test.ts` makes that comparison; a treatment that changes a section's
 * activity fails it.
 */
export const SECTION_TO_CASH_FLOW_ACTIVITY = Object.freeze({
    CURRENT_ASSETS: 'OPERATING',
    NON_CURRENT_ASSETS: 'INVESTING',
    CURRENT_LIABILITIES: 'OPERATING',
    NON_CURRENT_LIABILITIES: 'FINANCING',
    EQUITY: 'FINANCING',
    REVENUE: 'OPERATING',
    FINANCE_INCOME: 'OPERATING',
    OTHER_INCOME: 'OPERATING',
    COST_OF_SALES: 'OPERATING',
    OPERATING_EXPENSES: 'OPERATING',
    FINANCE_COSTS: 'OPERATING',
    TAXATION: 'OPERATING',
    OTHER_EXPENSES: 'OPERATING',
});
/**
 * Canonical display order for cash flow activities, matching the standard
 * direct-method presentation (operating first, then investing, then financing).
 */
export const CASH_FLOW_ACTIVITY_ORDER = Object.freeze([
    'OPERATING',
    'INVESTING',
    'FINANCING',
]);
/**
 * Human-readable labels for each `CashFlowActivity`. UK English, sentence case,
 * matching standard cash flow statement heading conventions.
 */
export const CASH_FLOW_ACTIVITY_LABELS = Object.freeze({
    OPERATING: 'Operating activities',
    INVESTING: 'Investing activities',
    FINANCING: 'Financing activities',
});
/**
 * How a ledger account's movements are PRESENTED in the cash flow statement.
 *
 * A refinement of `CashFlowActivity`: the four `OPERATING_*` values all narrow
 * to `OPERATING`, and name which block of the indirect statement's operating
 * section a balance-sheet movement belongs in. IFRS for SMEs §7.8(a) reserves
 * "changes in working capital" for inventories and operating receivables and
 * payables, so a provision, a deferred tax balance or an accrued interest
 * liability needs a block of its own rather than being swept in with them.
 *
 * `INVESTING` and `FINANCING` carry no sub-category: the indirect statement
 * presents each as a flat list of lines, so there is nothing to sub-divide.
 */
export const CashFlowTreatmentSchema = z.enum([
    'OPERATING_WORKING_CAPITAL',
    'OPERATING_NON_CASH',
    'OPERATING_TAX',
    'OPERATING_INTEREST',
    'INVESTING',
    'FINANCING',
]);
/**
 * Maps every `LedgerAccountSection` to its default `CashFlowTreatment`.
 *
 * Every entry narrows to the activity `SECTION_TO_CASH_FLOW_ACTIVITY` gives for
 * the same section, so widening the enum changed no report's grouping.
 *
 * Policy calls beyond that narrowing:
 *   - FINANCE_INCOME, FINANCE_COSTS -> OPERATING_INTEREST (§7.15 default)
 *   - TAXATION -> OPERATING_TAX (§7.17: taxes on income)
 *   - the current asset/liability and trading sections -> OPERATING_WORKING_CAPITAL,
 *     meaning "the ordinary operating cycle"
 *
 * Both new defaults are inert on their own: the indirect statement's movement
 * pass skips nominal accounts, so every line that reaches the tax and interest
 * blocks comes from a balance-sheet account somebody marked by hand. That is
 * also why `permittedCashFlowTreatmentsForAccount` withholds the four
 * `OPERATING_*` values on a nominal section - a default that reaches nothing
 * is fine, but offering an operator a recording that reaches nothing is not.
 */
export const SECTION_TO_CASH_FLOW_TREATMENT = Object.freeze({
    CURRENT_ASSETS: 'OPERATING_WORKING_CAPITAL',
    NON_CURRENT_ASSETS: 'INVESTING',
    CURRENT_LIABILITIES: 'OPERATING_WORKING_CAPITAL',
    NON_CURRENT_LIABILITIES: 'FINANCING',
    EQUITY: 'FINANCING',
    REVENUE: 'OPERATING_WORKING_CAPITAL',
    FINANCE_INCOME: 'OPERATING_INTEREST',
    OTHER_INCOME: 'OPERATING_WORKING_CAPITAL',
    COST_OF_SALES: 'OPERATING_WORKING_CAPITAL',
    OPERATING_EXPENSES: 'OPERATING_WORKING_CAPITAL',
    FINANCE_COSTS: 'OPERATING_INTEREST',
    TAXATION: 'OPERATING_TAX',
    OTHER_EXPENSES: 'OPERATING_WORKING_CAPITAL',
});
/**
 * Narrows a `CashFlowTreatment` to the `CashFlowActivity` it is presented
 * under. The only bridge between the two enums.
 */
export const CASH_FLOW_TREATMENT_TO_ACTIVITY = Object.freeze({
    OPERATING_WORKING_CAPITAL: 'OPERATING',
    OPERATING_NON_CASH: 'OPERATING',
    OPERATING_TAX: 'OPERATING',
    OPERATING_INTEREST: 'OPERATING',
    INVESTING: 'INVESTING',
    FINANCING: 'FINANCING',
});
/**
 * Human-readable labels for each `CashFlowTreatment`. UK English, sentence
 * case. These name the CLASSIFICATION an operator picks, not the statement's
 * block headings, which the presentation table owns.
 */
export const CASH_FLOW_TREATMENT_LABELS = Object.freeze({
    OPERATING_WORKING_CAPITAL: 'Operating – working capital',
    OPERATING_NON_CASH: 'Operating – non-cash adjustments',
    OPERATING_TAX: 'Operating – income tax',
    OPERATING_INTEREST: 'Operating – interest and dividends',
    INVESTING: 'Investing activities',
    FINANCING: 'Financing activities',
});
/**
 * Narrow a treatment to the activity it is grouped under.
 * Derived from `CASH_FLOW_TREATMENT_TO_ACTIVITY`; the frozen record is the
 * single source of truth.
 */
export function treatmentToCashFlowActivity(treatment) {
    return CASH_FLOW_TREATMENT_TO_ACTIVITY[treatment];
}
/**
 * Resolve a ledger account's cash flow treatment: the value recorded on the
 * account when it has one, otherwise the section-derived default.
 *
 * This is the single classification entry point. Reading
 * `account.cashFlowTreatment` directly ignores the default for every account
 * that has not been marked, which is nearly all of them.
 */
export function resolveCashFlowTreatment(account) {
    return account.cashFlowTreatment ?? SECTION_TO_CASH_FLOW_TREATMENT[account.section];
}
/**
 * Resolve a ledger account's cash flow activity - the narrowed, three-way
 * grouping both statements present. The composition of
 * `resolveCashFlowTreatment` and `treatmentToCashFlowActivity`.
 *
 * The direct method has only these three groups, and the indirect statement's
 * investing and financing sections are flat, so both want the narrowed value.
 * Only the indirect statement's operating section reads the treatment itself.
 */
export function resolveCashFlowActivity(account) {
    return treatmentToCashFlowActivity(resolveCashFlowTreatment(account));
}
/**
 * The `CashFlowTreatment` values that can actually change the statements for an
 * account with this `section` and `systemRole`, in `CashFlowTreatmentSchema`
 * order.
 *
 * Two independent narrowings, on the two axes the projections branch on:
 *
 *   - SECTION. A balance-sheet section takes all six. A NOMINAL section - every
 *     income and expense section - takes only `INVESTING` and `FINANCING`,
 *     because the four `OPERATING_*` values are inert there: the indirect
 *     statement's movement pass reads balance-sheet movements only, and its
 *     §7.8(c) reclassification pass emits nothing for an account whose
 *     treatment narrows to `OPERATING`.
 *   - SYSTEM ROLE. A role-classified account (`isCashFlowRoleClassified` - the
 *     cash-boundary and reconciling roles) takes NOTHING, because both
 *     statements decide what it presents as from the role and never read the
 *     classification at all. This axis is the stronger of the two: it withholds
 *     `INVESTING` and `FINANCING` as well, which the section axis never does.
 *
 * Marking an expense account `OPERATING_NON_CASH`, or a bank account anything
 * whatsoever, is accepted by the schema and changes no number - so an entry
 * surface that offers either is telling the operator something untrue.
 *
 * The named question form is `isCashFlowTreatmentPermittedForAccount`; both sit
 * beside `permittedSectionsForKind` / `isSectionPermittedForKind`, which answer
 * the same shape of question for sections on a ledger kind.
 */
export function permittedCashFlowTreatmentsForAccount(section, systemRole) {
    return CashFlowTreatmentSchema.options.filter((treatment) => isCashFlowTreatmentPermittedForAccount(section, systemRole, treatment));
}
/**
 * Whether `treatment` can change either statement for an account with this
 * `section` and `systemRole`. The yes/no form of
 * `permittedCashFlowTreatmentsForAccount` - a submit-time guard should ask this
 * rather than searching that array.
 */
export function isCashFlowTreatmentPermittedForAccount(section, systemRole, treatment) {
    if (isCashFlowRoleClassified(systemRole))
        return false;
    if (!isNominalSection(section))
        return true;
    return treatmentToCashFlowActivity(treatment) !== 'OPERATING';
}
// =============================================================================
// Ledger account system roles
// =============================================================================
// Mirrors `LedgerAccountSystemRole` in the Prisma schema. Role-bearing
// accounts (control accounts, VAT, default tags for fee / disbursement invoice
// lines, and the default business-expense account for unattributed supplier
// invoices) carry a non-null value; ordinary expense / income / asset rows are
// null. BANK, TRUST_BANK_S86_2, and TRUST_BANK_S86_4 accept many rows per
// ledger; every other role is singleton per ledger by virtue of the creation
// path (the chart-of-accounts preset is the only writer of non-BANK roles).
//
// ACCUMULATED_PROFIT is the anchor equity account that carries the
// opening/conversion balance and the nominal fold from prior financial years.
// Stamped on the "Retained Earnings" (law-firm) and "Owner's Capital Account"
// (sole-prop) rows in both chart-of-accounts presets.
//
// DEFAULT_EXPENSE is the general-purpose expense account (e.g. "General
// Expenses") used as the GL default for supplier-invoice lines with no
// explicit ledger account and no matter attribution.
//
// PAYMENTS_CLEARING is the in-transit current-asset account used by recorded-
// payment settlement, on BOTH a business and a trust ledger (a collapsed,
// kind-agnostic role - trust-ness is carried by the bank account + ledger,
// never the role label). Recording a payment against a ledgered invoice debits
// this account and credits AR; bank reconciliation later clears it. Every
// chart-of-accounts preset includes it so the settlement journal never
// 400s on a missing role.
//
// BANK_SUSPENSE is the per-bank holding account an imported bank statement
// line's counter-leg posts to at import time (`Dr Bank / Cr Bank Suspense`),
// kind-agnostic like PAYMENTS_CLEARING - trust-ness is carried by the bank
// account + ledger, never the role label. Suspense = bank confirmed, firm not
// yet categorised; Clearing = firm recorded, bank not yet confirmed. Nothing
// posts to it yet (introduced ahead of the import-posting change); it is
// provisioned everywhere PAYMENTS_CLEARING is.
//
// TRANSFER_CLEARING is the ledger-scoped bridge between independent
// bank-transfer legs. It is singleton and kind-agnostic, carries no
// bankAccountId, and is introduced ahead of the transfer-posting change. Its
// section is unconstrained on a BUSINESS ledger and locked to CURRENT_ASSETS on
// a TRUST ledger, the same posture PAYMENTS_CLEARING and BANK_SUSPENSE carry.
//
// BAD_DEBTS is the operating-expense account used to fund bad-debt write-off
// payments. Business ledgers only; singleton per ledger.
//
// DEFAULT_RECOVERY ("Recovery Income") is the income account a disbursement
// line with no expense behind it defaults to, so recoveries stay apart from fee
// income. It must sit in an income section (REVENUE, FINANCE_INCOME or
// OTHER_INCOME). Business ledgers only; singleton per ledger. A ledger without
// a holder falls back to the fee default.
// Trust roles (LPA s 86):
//   TRUST_BANK_S86_2             – pooled trust banking account (s 86(2))
//   TRUST_BANK_S86_3             – practice trust investment account (s 86(3))
//   TRUST_BANK_S86_4             – a single client's investment account (s 86(4))
//   TRUST_CREDITORS              – client trust creditors control account
//   TRUST_INTEREST_PAYABLE_LPFF  – LPFF interest payable (s 86(5))
//   TRUST_RECOVERABLE_FROM_BUSINESS – amount the business account owes the trust
//     account (an asset, normal balance debit): a bank charge levied on the
//     trust account that the Fidelity Fund does not carry (a firm-borne or
//     not-yet-attributed charge) leaves the trust position short by that
//     amount, and this account records the shortfall as a receivable from the
//     firm's own business account rather than understating the LPFF liability
//     - see LPBK 11 §1.7.4 ("additional accounts can be opened specifically
//     for this purpose"). It clears when the firm reimburses trust for the
//     charge. Distinct from TRUST_INTEREST_PAYABLE_LPFF, which stays reserved
//     for a genuine set-off against interest owed to the Fund.
//   TRUST_UNKNOWN_OWNER          – money held in trust whose owner is not yet
//     known (s 87(4)(a)): a liability like TRUST_CREDITORS, but untagged by
//     construction, since there is no client to tag it to. Singleton per
//     ledger; it clears as each deposit is identified and moved onto the named
//     client's trust creditor leg.
export const LedgerAccountSystemRoleSchema = z.enum([
    'BANK',
    'ACCOUNTS_RECEIVABLE',
    'ACCOUNTS_PAYABLE',
    'VAT_CONTROL',
    'DEFAULT_DISBURSEMENT',
    'DEFAULT_EXPENSE',
    'DEFAULT_FEE',
    'ACCUMULATED_PROFIT',
    'PAYMENTS_CLEARING',
    'BANK_SUSPENSE',
    'TRANSFER_CLEARING',
    'TRUST_BANK_S86_2',
    'TRUST_BANK_S86_3',
    'TRUST_BANK_S86_4',
    'TRUST_CREDITORS',
    'TRUST_INTEREST_PAYABLE_LPFF',
    'TRUST_RECOVERABLE_FROM_BUSINESS',
    'TRUST_UNKNOWN_OWNER',
    'ROUNDING',
    'BAD_DEBTS',
    'DEPRECIATION',
    'DEFAULT_RECOVERY',
]);
/**
 * Every role that makes a ledger account a BANK account: an ordinary business
 * bank (`BANK`), the firm's pooled trust banking account under s 86(2) of the
 * Legal Practice Act (`TRUST_BANK_S86_2`), the practice's own trust investment
 * account under s 86(3) (`TRUST_BANK_S86_3`), and a client investment account
 * under s 86(4) (`TRUST_BANK_S86_4`).
 *
 * They are one concept wherever code asks "is this row a bank?" - each carries
 * a `bankAccountId`, each is many-per-ledger, and each is provisioned and
 * archived by the same bank-link path alongside its paired
 * `PAYMENTS_CLEARING` and `BANK_SUSPENSE` rows. Testing only for `BANK` is a
 * silent trust-side omission rather than a visible failure: on a trust-split
 * practice the trust accounts simply disappear from whatever the caller is
 * doing, and the business side still looks right. A bare equality against one
 * or two of them fails the same way the next time a statutory type is added -
 * s 86(3) is expected - which is why this list, not a hand-spelled set, is the
 * thing to reach for.
 *
 * Exported because that test is written out by hand in the backend, the CLI,
 * the Bill UI, and the bookkeeper skill scripts.
 */
export const BANK_SYSTEM_ROLES = [
    'BANK',
    'TRUST_BANK_S86_2',
    'TRUST_BANK_S86_3',
    'TRUST_BANK_S86_4',
];
/**
 * Whether a system role makes its ledger account a bank account. A type
 * predicate, so a caller filtering legs by it carries the narrowed role
 * downstream without a cast.
 */
export function isBankSystemRole(role) {
    // `.some` rather than `.includes`: the list is typed as the narrow
    // `BankSystemRole`, which `includes` will not accept the wider argument for.
    // An equality comparison needs no widening cast.
    return role !== null && BANK_SYSTEM_ROLES.some((bankRole) => bankRole === role);
}
/**
 * The many-per-ledger bank-role accounts that may carry a `bankAccountId`,
 * mirroring the backend's `isManyPerLedgerRole` (`ledger.service.ts`): every
 * member of `BANK_SYSTEM_ROLES` plus the `PAYMENTS_CLEARING` and
 * `BANK_SUSPENSE` rows every bank link provisions alongside them. Spread from
 * `BANK_SYSTEM_ROLES` rather than spelled out, so a statutory type added later
 * widens this by construction - which is also why no count or member list is
 * repeated in this docblock. A bank account is linked onto exactly one of
 * these; every other role is singleton per ledger and the backend rejects a
 * `bankAccountId` set on it.
 *
 * Shared by the CLI (`account link-bank`'s target-role guard, `account
 * import`'s per-row validation, and `bank-transaction unmatch`'s
 * this-line's-own-bank-account internal-leg filter) and, eventually, the Bill
 * app's own copy (`BankStatementLineTransactions.tsx`'s
 * `internalLedgerAccountIds`, which currently diverges by failing closed).
 * Keep in sync with the backend list.
 */
export const MANY_PER_LEDGER_BANK_ROLES = [
    ...BANK_SYSTEM_ROLES,
    'PAYMENTS_CLEARING',
    'BANK_SUSPENSE',
];
/** Whether a system role is one of {@link MANY_PER_LEDGER_BANK_ROLES}. */
export function isManyPerLedgerBankRole(role) {
    return role !== null && MANY_PER_LEDGER_BANK_ROLES.includes(role);
}
/**
 * Merge ledger-entry legs that target the same ledger account into one leg
 * per account, summing their amounts and preserving first-seen order. Each
 * merged leg carries the first-seen leg's OTHER fields unchanged (id, kind,
 * label, and so on) with only `amountMinorUnits` replaced by the sum - so a
 * caller folding a richer leg shape than the bare `{ledgerAccountId,
 * amountMinorUnits}` pair does not lose the fields it did not ask to merge.
 *
 * A GL split's own leg builder produces one net leg plus a VAT_CONTROL leg
 * PER split, so a line (or a proposal) spread across two standard-rated
 * accounts builds two legs against the same VAT_CONTROL account. The
 * reconcile engine refuses two legs on one account outright ("Duplicate
 * ledger account in splits"); its own note is that they "collapse to one
 * anyway", so this performs that collapse deliberately at the point of use
 * rather than leaving it to surface only at the write. A pair whose amounts
 * cancel merges to a ZERO-amount leg rather than disappearing, so a caller's
 * non-empty / net-zero assertion still sees (and rejects) it - aggregation
 * must therefore run BEFORE those assertions, not after.
 *
 * The one fold shared by the CLI (`allocate apply`'s posted body,
 * `bank-transaction propose`'s persisted suggestion, and the `approve` plan's
 * preview render, each via the CLI's thin `aggregateSplitLegs` wrapper,
 * `apps/cli/src/ledger/core/splits.ts`) and the backend (`bank-line-proposal`
 * approve's customer-facing preview, via `aggregateProposalSplits`,
 * `bank-line-proposal.helpers.ts`, and the by-lines rehydration's edit-preview
 * and leg-list reconstruction). Both apps already depend on `@packages/types`,
 * and this is exactly the shape the CLI's dry-run plan must never diverge from
 * what the server persists, and the shape a proposal's customer-facing
 * previews must never diverge from what its own Approve button posts.
 */
export function aggregateLedgerSplitLegs(legs) {
    const firstSeenByAccount = new Map();
    const amountByAccount = new Map();
    for (const leg of legs) {
        if (!firstSeenByAccount.has(leg.ledgerAccountId)) {
            firstSeenByAccount.set(leg.ledgerAccountId, leg);
        }
        amountByAccount.set(leg.ledgerAccountId, (amountByAccount.get(leg.ledgerAccountId) ?? 0) + leg.amountMinorUnits);
    }
    return [...firstSeenByAccount].map(([ledgerAccountId, firstLeg]) => ({
        ...firstLeg,
        amountMinorUnits: amountByAccount.get(ledgerAccountId),
    }));
}
/**
 * The declared statutory character of a bank account - the single stored
 * discriminator on `BankAccount`, and the only one. Mirrors
 * `BankAccountStatutoryType` in the Prisma schema.
 *
 * Every question a caller used to ask of the retired business-vs-trust `kind`
 * is answered from this value instead: `ledgerKindForStatutoryType` for which
 * ledger the account's books live on, `bankRoleForStatutoryType` for which
 * bank role its GL account carries.
 *
 * Lives here rather than beside the other bank-account wire schemas because
 * the helpers below are what most callers actually want, and those are ledger
 * helpers.
 */
export const BankAccountStatutoryTypeSchema = z.enum(['BUSINESS', 'S86_2', 'S86_3', 'S86_4']);
/**
 * The bank-role a bank account's ledger representation carries, from its
 * statutory type. The single source of truth for that pairing, and the definition
 * `BankSystemRole` is derived from - so a statutory type added later cannot widen
 * one without widening the other.
 *
 * Read this where the question is "which bank role", and
 * `ledgerKindForStatutoryType` where it is "which ledger", so the s 86(2) /
 * s 86(4) distinction is never flattened by a caller that needed it.
 *
 * The switch is exhaustive with no `default`, so adding a member to
 * `BankAccountStatutoryType` is a compile error here rather than a silent
 * fall-through.
 */
export function bankRoleForStatutoryType(statutoryType) {
    switch (statutoryType) {
        case 'S86_2': return 'TRUST_BANK_S86_2';
        case 'S86_3': return 'TRUST_BANK_S86_3';
        case 'S86_4': return 'TRUST_BANK_S86_4';
        case 'BUSINESS': return 'BANK';
    }
}
/**
 * Trust investment account statutory types, as against the s 86(2) trust
 * banking account. Exhaustive so adding a future statutory type forces the
 * caller-facing rules to be reconsidered.
 */
export function isTrustInvestmentStatutoryType(statutoryType) {
    switch (statutoryType) {
        case 'S86_3':
        case 'S86_4':
            return true;
        case 'BUSINESS':
        case 'S86_2':
            return false;
    }
}
/**
 * The statutory types `isTrustInvestmentStatutoryType` accepts, as a list.
 * Derived from the schema's options rather than written out, so a new statutory
 * type joins (or stays out of) this list by the same predicate every caller
 * tests with. Typed to the narrowed union the predicate produces, so a consumer
 * that maps or filters over it keeps the narrowing rather than widening back to
 * every statutory type.
 */
export const TRUST_INVESTMENT_STATUTORY_TYPES = BankAccountStatutoryTypeSchema.options.filter(isTrustInvestmentStatutoryType);
/**
 * Whether a ledger account is a permitted TARGET for invoice or credit-note
 * income (re)allocation: an INCOME-type account (`sectionToType(section) ===
 * 'INCOME'` - REVENUE | FINANCE_INCOME | OTHER_INCOME), or the disbursement
 * account (`systemRole === 'DEFAULT_DISBURSEMENT'`) - the one deliberate
 * non-income target, since a disbursement line legitimately posts to it. The
 * disbursement role is matched by role ALONE, with no section check: a
 * disbursement account may be a current-asset (Disbursements Recoverable,
 * recovered on the next invoice) OR an expense (a disbursement written to
 * cost, not recovered), and the operator's chart decides which - accounting
 * is super-admin, dogfood-first, so the chart is trusted rather than guarded
 * (ledger domain § "Known permissive boundary (document; do not guard)"). Any
 * other target (AR, VAT Control, a bank account, a liability, equity, or a
 * plain non-disbursement expense account) is not permitted.
 *
 * Shared by the invoice domain's `reallocateInvoiceIncome`
 * (`invoice.service.reallocate.ts`) and the credit-note domain's
 * `createCreditNote` target-account guard (`credit-note.service.ts`) - the
 * two callers that move recognised income between GL accounts. Keep both on
 * this single predicate rather than inlining a second copy; each call site
 * still owns its own domain-specific rejection message.
 */
export function isPermittedIncomeTarget(section, systemRole) {
    return sectionToType(section) === 'INCOME' || systemRole === 'DEFAULT_DISBURSEMENT';
}
/**
 * Whether a cost recorded in the books backs an invoice line: the line was
 * billed from a supplier invoice (an expense), so it takes that cost's
 * clearing account rather than any role default. Every other line takes a role
 * default, decided by `pickLedgerAccountId` in the invoice domain.
 *
 * Shared by every GL-defaulting site that branches on a line's origin: the
 * backend null-line stamp and approve re-stamp, and the Bill draft editor's
 * suggested account. Keep them on this predicate so the screen and the
 * journal cannot drift apart.
 */
export function recoversRecordedCost(line) {
    return line.supplierInvoiceId != null;
}
/**
 * The one fee earner an invoice line's activities name: the shared
 * `performedByUserId` when every activity carries the same non-null one, else
 * null (no activities, an unassigned activity, or several earners mean the line
 * has no single performer). Shared by the backend GL stamp, the Word export and
 * the Bill draft editor so they agree on who earned a line.
 */
export function singlePerformerUserId(activities) {
    const performers = new Set(activities.map((activity) => activity.performedByUserId ?? null));
    if (performers.size !== 1)
        return null;
    return [...performers][0] ?? null;
}
/**
 * The account a disbursement with no expense behind it defaults to, given the
 * ledger's active DEFAULT_RECOVERY and DEFAULT_DISBURSEMENT holders: Recovery
 * Income when the ledger has it, else the default disbursement account only
 * when it is income (a firm that runs recoveries through one income account).
 * The usual default disbursement account is the Disbursements Recoverable
 * asset, which must never receive a line no expense backs. Null means the
 * line falls back to the fee rule.
 *
 * Shared by the backend line-default stamp and the Bill draft editor's
 * suggested account, so the screen and the journal cannot drift apart.
 */
export function effectiveRecoveryAccount(recoveryHolder, disbursementHolder) {
    if (recoveryHolder)
        return recoveryHolder;
    if (disbursementHolder && sectionToType(disbursementHolder.section) === 'INCOME')
        return disbursementHolder;
    return null;
}
/**
 * Whether a ledger account can be a fee earner's default fee income account:
 * an active INCOME-type account. Unlike `isPermittedIncomeTarget` it does not
 * admit `DEFAULT_DISBURSEMENT`, which is a disbursement target, not income.
 * Structural so it accepts both the wire shape (ISO string) and the database
 * row (Date). Which ledger the account sits on is the caller's check.
 */
export function isFeeEarnerFeeAccountCandidate(account) {
    return account.archivedAt === null && sectionToType(account.section) === 'INCOME';
}
// =============================================================================
// Ledger kind
// =============================================================================
// Discriminates a business ledger from a statutory trust ledger (LPA 28 of
// 2014 ss 84–92; LPC Rule 54). BUSINESS is the default. Mirrors `LedgerKind`
// in the Prisma schema.
export const LedgerKindSchema = z.enum(['BUSINESS', 'TRUST']);
/**
 * The ledger a bank account's books live on, from its statutory type: an
 * operating account on the BUSINESS ledger, every s 86 account on the TRUST
 * ledger.
 *
 * This is the business-vs-trust question that a stored `kind` column, and then
 * a derived `kind` field on the wire, used to answer. Both are gone: the
 * question is a property of the statutory type, so it is computed where it is
 * asked rather than carried alongside as a second value to keep true.
 *
 * Keyed on "anything but BUSINESS is trust" rather than a list of s 86
 * members, so a statutory type added later (s 86(3) is expected) is classified
 * correctly by construction rather than by remembering to extend this.
 *
 * Read this where the question is genuinely "which ledger" - which ledger to
 * resolve, whether an account belongs in a trust-only picker - and
 * `bankRoleForStatutoryType` where it is "which bank role". An s 86(2) pooled
 * account and an s 86(4) client investment account share a ledger but never a
 * role, so a caller that needs them apart must not ask this one.
 */
export function ledgerKindForStatutoryType(statutoryType) {
    return statutoryType === 'BUSINESS' ? 'BUSINESS' : 'TRUST';
}
/**
 * The `LedgerAccountSection` values a ledger of a given `LedgerKind` may
 * legitimately hold.
 *
 * TRUST is hand-spelled to exactly the three sections a statutory trust
 * ledger can carry, and no others:
 *
 * - `CURRENT_ASSETS` and `NON_CURRENT_ASSETS` hold the trust's own money and
 *   entrusted assets. Trust records are kept entirely separate from the
 *   practice's business records, so a trust ledger holds only what belongs to
 *   clients, never the practice's own income, expenses, or equity. Most trust
 *   holdings are short-term (the pooled trust bank, client investment
 *   accounts, in-transit clearing), but a long-dated fixed deposit or a
 *   non-cash entrusted asset can genuinely be held for longer than a year, so
 *   the non-current asset section stays available for that case.
 * - `CURRENT_LIABILITIES` holds what the trust owes back - money held for
 *   clients, interest due to the Fund, unclaimed balances. Trust assets must
 *   always reconcile to these trust-creditor balances, so nothing here is
 *   ever non-current: a trust creditor is repayable whenever the client to
 *   whom it is owed directs, not on a schedule the firm sets.
 *
 * Every other section is excluded because it belongs to the practice's own
 * affairs, not to money or property held for a client: `NON_CURRENT_LIABILITIES`
 * (a trust obligation is never long-term, as above), `EQUITY` (a non-zero
 * balance here would assert the firm owns trust money, which it never does),
 * and every income and expense section (revenue, costs, and interest earned
 * or incurred are the practice's own transactions, settled through the
 * business books, not recorded against the trust).
 *
 * BUSINESS is derived from `LEDGER_SECTION_ORDER` rather than hand-spelled, so
 * a section added later is included on a business ledger and excluded from a
 * trust ledger by construction, without this constant needing an edit.
 */
const PERMITTED_SECTIONS_BY_LEDGER_KIND = Object.freeze({
    TRUST: Object.freeze(['CURRENT_ASSETS', 'CURRENT_LIABILITIES', 'NON_CURRENT_ASSETS']),
    BUSINESS: Object.freeze([...LEDGER_SECTION_ORDER]),
});
/**
 * Permitted sections for `kind`, in `LEDGER_SECTION_ORDER` order.
 */
export function permittedSectionsForKind(kind) {
    return LEDGER_SECTION_ORDER.filter((section) => PERMITTED_SECTIONS_BY_LEDGER_KIND[kind].includes(section));
}
/**
 * Whether `section` is a permitted section for a ledger of `kind`. The named
 * question form for the same table `permittedSectionsForKind` filters -
 * callers that only need a yes/no answer (a submit-time guard) should ask
 * this rather than reaching into `PERMITTED_SECTIONS_BY_LEDGER_KIND` directly.
 */
export function isSectionPermittedForKind(kind, section) {
    return PERMITTED_SECTIONS_BY_LEDGER_KIND[kind].includes(section);
}
// =============================================================================
// Slim ledger-account shape embedded in the provisioned-account summary.
// The full LedgerAccountSchema is declared further down in this file; the
// admin detail page only needs the columns it renders, so we declare a
// scoped shape here rather than forward-referencing the full schema.
// =============================================================================
export const ProvisionedAccountLedgerAccountSchema = z.object({
    id: z.string(),
    ledgerId: z.string(),
    code: z.string(),
    name: z.string(),
    section: LedgerAccountSectionSchema,
    systemRole: LedgerAccountSystemRoleSchema.nullable(),
    archivedAt: z.string().nullable(),
});
// =============================================================================
// Ledger
// =============================================================================
export const LedgerSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    kind: LedgerKindSchema,
    baseCurrency: z.string().length(3),
    openingDate: z.string().nullable(),
    financialYearEndMonth: z.number().int().min(1).max(12).nullable(),
    vatFilingCategory: VatFilingCategorySchema.nullable(),
    // True once the bulk-import conversion entry set has been posted
    // (`opening-balance-<ledgerId>`). The frontend uses this to determine whether
    // the opening date is still editable (false = editable; true = locked).
    openingBalancesPosted: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** A ledger mutation plus conduct warnings from business-entity settings. */
export const LedgerWriteResponseSchema = LedgerSchema.extend(writeWarningsFields);
export const LedgerAccountSchema = z.object({
    id: z.string(),
    ledgerId: z.string(),
    accountId: z.string(),
    code: z.string(),
    name: z.string(),
    section: LedgerAccountSectionSchema,
    systemRole: LedgerAccountSystemRoleSchema.nullable(),
    bankAccountId: z.string().nullable(),
    defaultVatTreatment: VatTreatmentSchema.nullable(),
    // Null = derive the treatment from `section`. See `resolveCashFlowTreatment`.
    cashFlowTreatment: CashFlowTreatmentSchema.nullable(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const LedgerEntrySchema = z.object({
    id: z.string(),
    entrySetId: z.string(),
    ledgerAccountId: z.string(),
    accountId: z.string(),
    clientId: z.string().nullable(),
    supplierId: z.string().nullable(),
    invoiceId: z.string().nullable(),
    supplierInvoiceId: z.string().nullable(),
    creditNoteId: z.string().nullable(),
    bankTransferId: z.string().nullable(),
    clientProjectId: z.string().nullable(),
    amountMinorUnits: z.number().int(),
    currency: z.string().length(3),
    createdAt: z.string(),
});
export const LedgerEntrySetSchema = z.object({
    id: z.string(),
    ledgerId: z.string(),
    accountId: z.string(),
    accountingDate: z.string(),
    description: z.string(),
    postedByUserId: z.string().nullable(),
    idempotencyKey: z.string(),
    /**
     * Why this journal was posted. The journals UI switches on it to decide
     * where the row's source-document link points - see `sourceDomainOf` and
     * `counterpartyDomainOf` in `ledger-intent.ts`.
     */
    intent: LedgerIntentSchema,
    /**
     * The record in `sourceDomainOf(intent)`'s domain that this journal was
     * posted for, or null when the intent declares no source (a manual journal,
     * an opening balance, the backfill sentinel). Never "sometimes set": the
     * posting choke point enforces both directions, so a null here means "this
     * journal has no source document", not "nobody filled it in".
     */
    sourceEntityId: z.string().nullable(),
    reversedEntrySetId: z.string().nullable(),
    createdAt: z.string(),
    entries: z.array(LedgerEntrySchema),
});
/**
 * `POST .../entry-sets/:entrySetId/reverse`'s response: the posted reversal
 * plus the optional `warnings`/`warningDetails` pair, carrying the conduct
 * warning raised when the reversed journal is an asset's linked acquisition
 * (see `acquisitionReversalWarningForEntrySet` in the backend's asset domain).
 */
export const ReverseLedgerEntrySetResponseSchema = LedgerEntrySetSchema.extend(writeWarningsFields);
/**
 * The idempotency-key namespace a reversed ORIGINAL entry set is renamed into.
 *
 * `reverseLedgerEntrySet` renames the original's key to
 * `reversed-original:${original.id}` once its reversal has posted, which frees
 * the original's `(ledgerId, idempotencyKey)` slot for a later re-post. Lives
 * here rather than in the backend service because both sides of the wire read
 * it: the service uses it to write the sentinel and to detect a partial-failure
 * retry, and the journals UI uses it to tell an already-reversed journal from a
 * live one. Duplicating the literal on the frontend would let the two drift on
 * a rename that no test could catch.
 */
export const REVERSED_ORIGINAL_IDEMPOTENCY_KEY_PREFIX = 'reversed-original:';
/**
 * True when this entry set is an ORIGINAL that has already been reversed.
 *
 * `LedgerEntrySet.reversedEntrySetId` is set only on the REVERSAL row (pointing
 * back at what it reverses), never on the original, so there is no "has this
 * been reversed" column to read. The sentinel key is the signal, and it encodes
 * the original's own id - so `entrySet` must be the set being CHECKED, not its
 * reversal. Pure: no I/O, never throws.
 */
export function isReversedOriginal(entrySet) {
    return entrySet.idempotencyKey === `${REVERSED_ORIGINAL_IDEMPOTENCY_KEY_PREFIX}${entrySet.id}`;
}
export const LedgerListResponseSchema = z.object({
    data: z.array(LedgerSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/**
 * Response for DELETE /accounts/:accountId/ledgers/:ledgerId (the empty-ledger
 * hard delete used to undo a failed onboarding). Reports the deleted ledger and
 * how many chart-of-accounts rows went with it.
 */
export const DeleteEmptyLedgerResponseSchema = z.object({
    deletedLedgerId: z.string(),
    deletedLedgerAccountCount: z.number().int().nonnegative(),
});
/**
 * Response for GET /accounts/:accountId/ledger-status.
 * `hasActiveLedger` is true when the account has an active BUSINESS ledger.
 * `hasActiveTrustLedger` is true when the account has an active TRUST ledger.
 * Member-readable; the Bill UI uses it to gate the client-statement surfaces
 * (the client hub's "Client statement" button, the statement page's Trust
 * view, and the "Funds held" figure) without firing a request it expects to
 * 404 as the detection signal.
 */
export const LedgerStatusResponseSchema = z.object({
    hasActiveLedger: z.boolean(),
    hasActiveTrustLedger: z.boolean(),
});
/**
 * Response for `GET .../clients/:clientId/outstanding-balance`. Positive means
 * the client owes the firm (a debit balance on Accounts Receivable); negative
 * means the client is in credit.
 *
 * Current backend responses return both fields populated, or fail when the
 * account has no active BUSINESS ledger. The nullable shape is retained for
 * clients that may still read the earlier no-ledger response contract, where
 * both fields were null together.
 */
export const ClientOutstandingBalanceResponseSchema = z.object({
    outstandingBalanceMinorUnits: z.number().nullable(),
    outstandingCurrency: z.string().nullable(),
});
export const LedgerAccountListResponseSchema = z.object({
    data: z.array(LedgerAccountSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/**
 * Query params for GET .../ledgers/:ledgerId/accounts. `includeArchived`
 * defaults to false (omitted or `false` returns active accounts only,
 * matching every existing caller's behaviour) - a picker or a filter dropdown
 * must never see an archived row. Set `true` only when the caller needs to
 * resolve a historical (now-archived) account by id, e.g. a journal leg
 * naming an account that has since been archived.
 */
export const ListLedgerAccountsQuerySchema = z.object({
    includeArchived: BooleanQueryParamSchema.optional(),
    // Narrow the chart to named sections (repeated `section` params, as `ids` on
    // the calendar-events list). Omitted returns every section, so every existing
    // caller is unchanged. Exists so a caller that only wants one slice of the
    // chart says so in the query rather than fetching all of it and filtering
    // client-side - the chat agent resolving an expense category is the driving
    // case (see `sectionToType` for which sections make up EXPENSE), and the
    // frontend's expense-account pickers are the other filter-after-fetch site.
    section: z.array(LedgerAccountSectionSchema).min(1).optional(),
    // Narrow the chart to a single system role. Omitted returns every role, so
    // every existing caller is unchanged. The cashbook's bank-account filter is
    // the driving case (systemRole=BANK, including archived).
    systemRole: LedgerAccountSystemRoleSchema.optional(),
    // Narrow the chart to named accounts (repeated `ids` params), the house
    // list-with-ids lookup. Archived accounts among them are still excluded
    // unless includeArchived is true. Omitted returns every account.
    ids: z.array(LowercaseUuidSchema).min(1).max(100).optional(),
});
/**
 * Response for GET .../accounts/:ledgerAccountId/balance. The ALL-TIME signed
 * balance of the account (debit-positive), unlike the trial balance's nominal
 * (INCOME/EXPENSE) accounts, which are scoped to the current financial year -
 * see `TrialBalanceSchema`. Used where the caller needs the true posted total
 * regardless of financial-year boundaries, e.g. the `account archive --force`
 * dry-run, which must show the same all-time figure the archive guard checks.
 */
export const LedgerAccountBalanceResponseSchema = z.object({
    balanceMinorUnits: z.number().int(),
    currency: z.string().length(3),
});
export const LedgerEntrySetListResponseSchema = z.object({
    data: z.array(LedgerEntrySetSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/**
 * A ledger-account filter that accepts either a single value
 * (`?ledgerAccountIds=a`) or repeated params (`?ledgerAccountIds=a&ledgerAccountIds=b`),
 * parsed as `string | string[]`. Capped at 100 entries.
 *
 * Fastify's querystring parser yields a bare string for one occurrence and an
 * array for several, so the union is the wire shape, not a convenience. Collapse
 * it with `toLedgerAccountIdList` rather than branching on `Array.isArray` at
 * each call site. A `.transform()` on the schema would NOT work here: AJV
 * validates the JSON Schema `z.toJSONSchema` emits, which cannot encode a
 * transform, and the framework's re-parse would then run it twice (see
 * `api/CLAUDE.md` on transforms at the Zod/ajv boundary).
 *
 * Shared by the general-ledger report and the journals entry-set list so the
 * two ledger reads express a multi-account filter identically.
 */
const LedgerAccountIdListQueryParamSchema = z
    .union([z.uuid(), z.array(z.uuid()).max(100)])
    .optional();
/** Collapse the `string | string[]` wire shape into a plain list. */
export function toLedgerAccountIdList(value) {
    if (value === undefined)
        return undefined;
    return Array.isArray(value) ? value : [value];
}
/**
 * Sortable columns on the journals (entry-set) list.
 *
 * Deliberately excludes the journal's total: that figure is the debit side
 * AFTER netting each account's legs within a set (a clearing-hinged bank
 * movement posts an equal Dr and Cr that must not be double-counted), so it is
 * a derived per-set computation over the legs, not a column the database can
 * order by. Sorting it would mean either draining every page to the client or
 * sorting only the pages already loaded - both wrong under cursor pagination.
 */
export const LedgerEntrySetSortBySchema = z.enum(['accountingDate', 'description', 'postedBy']);
export const LedgerEntrySetSortDirectionSchema = z.enum(['asc', 'desc']);
// Named period windows the financial reports accept in place of explicit
// from/to dates. The backend resolves a preset to concrete YYYY-MM-DD dates
// using the ledger's financial-year-end month and the UTC calendar date for
// "today"; the frontend sends only the preset id.
export const PERIOD_PRESETS = [
    'this-month',
    'last-month',
    'this-quarter',
    'last-quarter',
    'this-financial-year',
    'last-financial-year',
    'last-12-months',
];
export const PeriodPresetSchema = z.enum(PERIOD_PRESETS);
/**
 * Query params for the paginated entry-set list. Cursor + limit come from the
 * shared `PaginationQuerySchema`. Filters:
 *   - `ledgerAccountIds`: only entry sets with at least one leg on ANY of these
 *     accounts (repeatable; see `LedgerAccountIdListQueryParamSchema`).
 *   - `from` / `to`: inclusive accounting-date bounds (YYYY-MM-DD).
 *   - `period`: backend-resolved preset alternative to date bounds.
 *   - `search`: case-insensitive substring match on the entry-set description.
 *   - `excludeReversed`: omitted or `false` returns every entry set, matching
 *     every existing caller's behaviour. `true` drops both a reversal mirror
 *     (`reversedEntrySetId !== null`) and the original it reversed (its
 *     idempotency key carries the `reversed-original:` sentinel - see
 *     `isReversedOriginal`), so a picker built for LIVE journals only (e.g. the
 *     asset-acquisition journal picker) gets full pages instead of one thinned
 *     by client-side filtering after the fact.
 *
 * Sorting defaults to `accountingDate` descending (newest journals first), the
 * behaviour every caller had before sorting was exposed. Every sort is applied
 * server-side and carries `id asc` as its tiebreaker in the repository, which
 * is what keeps cursor pagination stable under a non-default order.
 */
export const LedgerEntrySetListQuerySchema = PaginationQuerySchema.extend({
    ledgerAccountIds: LedgerAccountIdListQueryParamSchema,
    from: LedgerDateOnlySchema.optional(),
    to: LedgerDateOnlySchema.optional(),
    period: PeriodPresetSchema.optional(),
    search: z.string().max(200).optional(),
    excludeReversed: BooleanQueryParamSchema.optional(),
    sortBy: LedgerEntrySetSortBySchema.optional().prefault('accountingDate'),
    sortDirection: LedgerEntrySetSortDirectionSchema.optional().prefault('desc'),
}).refine((q) => q.period === undefined || (q.from === undefined && q.to === undefined), {
    message: 'Provide either period, or date bounds',
});
export const LedgerJournalsExportQuerySchema = z.object({
    from: LedgerDateOnlySchema.optional(),
    to: LedgerDateOnlySchema.optional(),
});
// =============================================================================
// Ledger Entry Set Documents
// =============================================================================
export const LedgerEntrySetDocumentResponseSchema = z.object({
    id: z.string(),
    ledgerEntrySetId: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    downloadUrl: z.string(),
});
export const LedgerEntrySetDocumentListResponseSchema = z.array(LedgerEntrySetDocumentResponseSchema);
// =============================================================================
// Report date presets
//
// Named as-at windows the financial reports accept in place of explicit dates.
// The backend resolves a preset to concrete YYYY-MM-DD date(s) using the
// ledger's financial-year-end month and the UTC calendar date for "today"; the
// frontend sends only the preset id. Period reports (income statement, cash
// flow, and the general-ledger account drill-in) take a from/to window via
// `PeriodPreset`; as-at reports (balance sheet, trial balance) take a single
// date via `AsOfPreset`.
//
// The five FY-dependent presets (`this-quarter`, `last-quarter`,
// `this-financial-year`, `last-financial-year`, `end-of-last-financial-year`)
// only resolve when the ledger has a `financialYearEndMonth`; the backend
// rejects them with a 400 otherwise.
// =============================================================================
export const AS_OF_PRESETS = [
    'today',
    'end-of-last-month',
    'end-of-last-financial-year',
];
export const AsOfPresetSchema = z.enum(AS_OF_PRESETS);
/**
 * Resolve a period report's raw querystring (`{ from?, to?, period? }`) to a
 * `PeriodReportRange`. The query schema's cross-field refine guarantees
 * exactly one mode is present - REQUIRED, not optional, so a schema whose
 * refine allows neither (e.g. a report where the window truly is optional)
 * must not call this; see `optionalAsOfReportInputFromQuery` for that shape's
 * as-at sibling. Pure and deterministic: no I/O, so it needs no backend
 * dependency to live beside the schemas it derives over (`packages/types`
 * CLAUDE.md, form 2). The resolved shape is a plain union structurally
 * assignable to the backend's own (FYE-resolution-shaped) `PeriodReportRange`
 * in `domains/ledger/ledger.service.ts` - callers pass the result straight
 * into `resolvePeriodReportRange`/`getIncomeStatement`/`getCashFlow` with no
 * cast needed.
 */
export function periodReportRangeFromQuery(query) {
    return query.period !== undefined ? { period: query.period } : { from: query.from, to: query.to };
}
/**
 * Resolve an as-at report's raw querystring to an `AsOfReportInput`, for a
 * report whose window is REQUIRED to be exactly one of `asOf` / `asOfPreset`
 * (balance sheet, both age analyses). See `optionalAsOfReportInputFromQuery`
 * for the tri-state sibling (trial balance, trust creditors).
 */
export function asOfReportInputFromQuery(query) {
    return query.asOf !== undefined ? { asOf: query.asOf } : { asOfPreset: query.asOfPreset };
}
/**
 * Resolve an as-at report's raw querystring to an `AsOfReportInput`, or
 * `undefined` when neither is present, for a report whose as-at window is
 * OPTIONAL (trial balance, trust reconciliation): explicit date, preset, or
 * `undefined` (the report's own all-time / current-FY default).
 */
export function optionalAsOfReportInputFromQuery(query) {
    return query.asOf !== undefined
        ? { asOf: query.asOf }
        : query.asOfPreset !== undefined
            ? { asOfPreset: query.asOfPreset }
            : undefined;
}
// =============================================================================
// Report period sets
//
// A period SET is the input to a multi-column report: one request resolves to
// several date windows at once (a column per month, plus - for the
// financial-year set - a year-to-date window and its optional comparatives).
// This is the report-agnostic vocabulary. It says nothing about which report
// consumes it: a flow reader (income statement, cash flow) sums each window,
// a stock reader (balance sheet) takes a position at each window's `to`, and
// the opening position is the prior full year's `to`.
//
// The wire encoding is flat (querystrings cannot nest), but the resolved input
// is a discriminated union so an incoherent combination is unrepresentable
// past the boundary. `rolling` carries no comparatives and no year-to-date - a
// rolling window may straddle the financial-year start, where both are
// meaningless. `financial-year` carries the opt-in comparatives.
//
// The backend resolves a set to concrete YYYY-MM-DD windows in
// `domains/ledger/ledger-period-set.ts`, using the ledger's financial-year-end
// month and the UTC calendar date for "today"; the frontend sends only the
// month identifiers.
// =============================================================================
/**
 * A calendar month identifier, `YYYY-MM`.
 *
 * Spelled once and shared by every period-set field and response, so a
 * consumer can never re-spell the regex slightly differently. Month-aligned
 * by construction: there is no day component to widen into a mid-month
 * cutoff.
 *
 * The year band excludes 0000-0099: `Date.UTC` (and the legacy `Date`
 * constructor generally) treats a two-digit year as shorthand for 1900-1999,
 * so a caller-supplied year in that band would silently resolve to a
 * different century a hundred-plus years away rather than the year it
 * actually named. Rejecting the band at the wire boundary keeps every
 * accepted `YearMonth` representable by plain `new Date(Date.UTC(year, ...))`
 * arithmetic without remapping - four-digit years from 0100 upward are
 * unaffected and continue to parse.
 */
export const YearMonthSchema = z.string().regex(/^(?!00\d{2}-)\d{4}-(0[1-9]|1[0-2])$/, {
    error: 'Expected a calendar month (YYYY-MM)',
});
export const REPORT_PERIOD_SETS = ['rolling', 'financial-year'];
export const ReportPeriodSetKindSchema = z.enum(REPORT_PERIOD_SETS);
const ReportPeriodSetMonthCountQuerySchema = z.string()
    .regex(/^(?:[2-9]|1[0-2])$/, {
    error: 'monthCount must be a decimal integer from 2 to 12',
})
    .transform((value) => Number(value));
/**
 * Querystring shape for a period-set report. Flat because a querystring cannot
 * nest, with a cross-field refine standing in for the discriminated union: a
 * field is legal only under the `periodSet` that gives it meaning. The refine
 * is what lets `reportPeriodSetInputFromQuery` map to the union without
 * inventing a precedence rule for combinations that never reach it.
 *
 * `monthCount` is bounded 2..12 rather than 1..12: a one-month "series" is the
 * existing single-period report, and the upper bound caps the aggregate the
 * consumer has to run.
 */
export const ReportPeriodSetQuerySchema = z
    .object({
    periodSet: ReportPeriodSetKindSchema,
    /** Rolling only. The newest month in the window; defaults to the latest completed month. */
    endingMonth: YearMonthSchema.optional(),
    /** Rolling only. How many complete calendar months the window spans; defaults to 12. */
    monthCount: ReportPeriodSetMonthCountQuerySchema.optional(),
    /** Financial-year only. The last month of the year-to-date window; defaults to the latest completed month. */
    cutoffMonth: YearMonthSchema.optional(),
    /** Financial-year only. Include the prior year-to-date and prior full-year windows. */
    comparatives: BooleanQueryParamSchema.optional(),
})
    .refine((query) => query.periodSet !== 'rolling' ||
    (query.cutoffMonth === undefined && query.comparatives === undefined), { error: 'cutoffMonth and comparatives are only valid when periodSet is financial-year' })
    .refine((query) => query.periodSet !== 'financial-year' ||
    (query.endingMonth === undefined && query.monthCount === undefined), { error: 'endingMonth and monthCount are only valid when periodSet is rolling' });
/**
 * Resolve a period-set report's raw querystring to a `ReportPeriodSetInput`.
 *
 * Pure and deterministic - no I/O, no clock - so it lives beside the schema it
 * derives over (`packages/types` CLAUDE.md, form 2) and both the route and the
 * API client can use it. It drops the fields the other variant owns rather
 * than trusting them to be absent; `ReportPeriodSetQuerySchema`'s refine has
 * already rejected any request that carried them, so nothing legal is lost.
 * Precedent: `periodReportRangeFromQuery`.
 */
export function reportPeriodSetInputFromQuery(query) {
    return query.periodSet === 'rolling'
        ? { periodSet: 'rolling', endingMonth: query.endingMonth, monthCount: query.monthCount }
        : { periodSet: 'financial-year', cutoffMonth: query.cutoffMonth, comparatives: query.comparatives };
}
/**
 * Querystring for the income-statement series exports
 * (`income-statement-series.pdf` / `.xlsx`). Chains off
 * `ReportPeriodSetQuerySchema` directly with `.extend()` - in the installed
 * zod 4.4.3 a refined `ZodObject` still exposes `.extend()`, so the two
 * period-set refines above are inherited rather than re-stated, and this
 * schema adds only what is genuinely new: `breakdown`.
 *
 * One period-set query shape serves two on-screen layouts (Monthly, and
 * Total + Compare), rendering a different column set for the same resolved
 * windows - `breakdown` tells the export which one to render, mirroring
 * `incomeStatementSeriesColumns`'s own two branches. `'total'` only makes
 * sense over the financial-year comparative cluster (there is no "Total"
 * view of a rolling window), so it is additionally pinned to
 * `periodSet: 'financial-year'` with `comparatives: true`.
 */
/**
 * Which on-screen layout an income-statement series export should render.
 * Named here rather than inline so the frontend and the export fetchers share
 * one canonical value set - they previously borrowed the balance sheet's
 * breakdown type, which stopped meaning anything once that report moved to
 * date-anchored columns with no breakdown axis at all.
 */
export const IncomeStatementSeriesBreakdownSchema = z.enum(['monthly', 'total']);
export const IncomeStatementSeriesExportQuerySchema = ReportPeriodSetQuerySchema.extend({
    breakdown: IncomeStatementSeriesBreakdownSchema,
}).refine((query) => query.breakdown !== 'total' || (query.periodSet === 'financial-year' && query.comparatives === true), { error: 'breakdown "total" requires periodSet "financial-year" and comparatives true' });
// =============================================================================
// Trial Balance
// =============================================================================
/**
 * A single row in a trial balance: one ledger account with its net signed
 * balance. `balanceMinorUnits` is positive for net debit, negative for net
 * credit.
 */
export const TrialBalanceAccountSchema = z.object({
    ledgerAccountId: z.string(),
    code: z.string(),
    name: z.string(),
    section: LedgerAccountSectionSchema,
    systemRole: LedgerAccountSystemRoleSchema.nullable(),
    archivedAt: z.string().nullable(),
    balanceMinorUnits: z.number().int(),
});
/**
 * Trial balance for a ledger. Only includes visible accounts: active accounts
 * always appear; inactive accounts only appear when `balanceMinorUnits !== 0`.
 *
 * `debitTotalMinorUnits` is the sum of all positive balances.
 * `creditTotalMinorUnits` is the sum of the absolute values of all negative
 * balances. In a balanced ledger these two values are equal.
 */
export const TrialBalanceSchema = z.object({
    // ISO date-time, end of the as-at day UTC (matching BalanceSheet's asOf
    // shape). `null` only on a ledger with no financial year end set, where the
    // all-time balance is returned. When a financial year end IS set, an omitted
    // asOf resolves to today, so this carries today's end-of-day cutoff.
    asOf: z.string().nullable(),
    // YYYY-MM-DD start of the financial year the nominal (INCOME/EXPENSE) account
    // balances are scoped to; `null` when no FY-scoping was applied (ledger has no
    // financial year end set). Per-account ledger views (e.g. account detail) read
    // this to scope a nominal account's journal to the same window the balance
    // covers, so the running balance reconciles with the displayed balance.
    fyStartDate: z.string().nullable(),
    accounts: z.array(TrialBalanceAccountSchema),
    currency: z.string().length(3),
    debitTotalMinorUnits: z.number().int().nonnegative(),
    creditTotalMinorUnits: z.number().int().nonnegative(),
});
/**
 * The trial balance at several dates at once, oldest first, the anchor date
 * last. Every column is a full `TrialBalance` as at its own date: the series
 * adds no new figures, only more dates of the shape the single-date endpoint
 * already returns.
 *
 * FLAT, not grouped - deliberately, because the trial-balance SCREEN is flat.
 * `TrialBalancePage` renders `getTrialBalance`'s accounts sectioned by
 * `LEDGER_SECTION_ORDER` in the component; account groups belong to the balance
 * sheet and income statement, which have their own `grouped-*` readers. A
 * grouped trial-balance series would be a shape nothing renders.
 *
 * Each column carries its own `debitTotalMinorUnits` / `creditTotalMinorUnits`
 * and balances on its own. There is no series-wide total, and there must not
 * be: a trial-balance figure is a cumulative balance AT a date, so adding the
 * columns would count the same balance once per column. What the multi-date
 * view buys is the ability to see WHICH date a ledger stopped balancing on,
 * not a bigger total.
 *
 * `fyStartDate` is per column too. Columns spanning a financial year end carry
 * different FY starts, which is correct and is exactly why nominal accounts can
 * legitimately fall rather than rise between two adjacent columns.
 *
 * The 2..13 bound is the balance-sheet series' bound, for the same reason: one
 * anchor plus at most twelve earlier month ends.
 */
/**
 * The most dated columns an as-at series can carry: the anchor plus the twelve
 * earlier month ends `previous-12` walks back. Every as-at series response is
 * bounded by it, so a new one cannot quietly pick a different ceiling.
 *
 * The floor is 2 because a one-column as-at report IS the single-date report,
 * which has its own endpoint and its own renderer.
 */
export const MAX_AS_OF_SERIES_COLUMNS = 13;
export const MIN_AS_OF_SERIES_COLUMNS = 2;
export const TrialBalanceSeriesColumnSchema = TrialBalanceSchema.extend({
    /**
     * Non-null here, unlike on the single-date `TrialBalanceSchema`. That report
     * admits a null `asOf` for the all-time balance a ledger with no financial
     * year end returns when no cutoff is asked for. A series ALWAYS has a
     * resolved anchor - it is what the columns are dates of - so a null would be
     * a column with no date to label it, which cannot arise and must not be
     * representable.
     */
    asOf: z.string(),
});
export const TrialBalanceSeriesSchema = z.object({
    columns: z.array(TrialBalanceSeriesColumnSchema).min(MIN_AS_OF_SERIES_COLUMNS).max(MAX_AS_OF_SERIES_COLUMNS),
});
/**
 * Query params for the trial-balance endpoint. Supply EITHER an explicit
 * `asOf` date OR an `asOfPreset`, never both. When neither is supplied, a
 * ledger with a financial year end returns the balance as at today with
 * nominal (INCOME/EXPENSE) accounts scoped to the current financial year
 * (real accounts stay cumulative); a ledger with no financial year end set
 * returns the all-time balance. The backend resolves a preset to a concrete
 * date.
 *
 * `showZeroBalances` is tri-state and orthogonal to the asOf/asOfPreset
 * exclusivity above:
 *   - omitted or `true` – active zero-balance accounts are shown (today's
 *     default behaviour). The Accounts tab / chart-of-accounts list relies on
 *     this default to keep freshly-created zero-balance accounts visible, so
 *     it always omits the field.
 *   - `false` – active zero-balance accounts are hidden. Non-zero archived
 *     accounts remain visible either way (the accounting-equation rule is
 *     unconditional).
 */
export const TrialBalanceQuerySchema = z
    .object({
    asOf: LedgerDateOnlySchema.optional(),
    asOfPreset: AsOfPresetSchema.optional(),
    showZeroBalances: BooleanQueryParamSchema.optional(),
})
    .refine((q) => !(q.asOf !== undefined && q.asOfPreset !== undefined), {
    message: 'Provide either asOf or asOfPreset, not both',
});
// =============================================================================
// General Ledger
// =============================================================================
export const GeneralLedgerNaturalSideSchema = z.enum(['DEBIT', 'CREDIT']);
export const GeneralLedgerGroupKindSchema = z.enum(['ACCOUNT', 'TRUST_CLIENT']);
export const GeneralLedgerSourceTypeSchema = z.enum([
    'MANUAL_JOURNAL',
    'REVERSAL',
    'BANK_ALLOCATION',
    'BANK_IMPORT',
    'INVOICE',
    'SUPPLIER_INVOICE',
    'CREDIT_NOTE',
    'PAYMENT',
]);
export const GeneralLedgerSourceSchema = z.object({
    type: GeneralLedgerSourceTypeSchema,
    id: z.uuid().nullable(),
});
export const GeneralLedgerQuerySchema = z
    .object({
    from: LedgerDateOnlySchema.optional(),
    to: LedgerDateOnlySchema.optional(),
    period: PeriodPresetSchema.optional(),
    ledgerAccountIds: LedgerAccountIdListQueryParamSchema,
})
    .refine((q) => (q.period !== undefined && q.from === undefined && q.to === undefined) ||
    (q.period === undefined && q.from !== undefined && q.to !== undefined), { message: 'Provide either period, or both from and to' });
/**
 * Query for the single-account general-ledger export (served by the
 * `api/routes/report-export/` routes, built in `domains/financial-reports/`).
 * Unlike `GeneralLedgerQuerySchema`, which allows zero, one, or many
 * `ledgerAccountIds` (an omitted filter means "every account"), an export
 * always narrows to exactly one account: the date window is either a backend-
 * resolved period preset or explicit `from`/`to`, and `ledgerAccountId` is a
 * single required uuid, not a list. Zero or multiple accounts is therefore a
 * plain 400 at the HTTP boundary rather than a service-level ambiguity.
 */
export const GeneralLedgerExportQuerySchema = z
    .object({
    from: LedgerDateOnlySchema.optional(),
    to: LedgerDateOnlySchema.optional(),
    period: PeriodPresetSchema.optional(),
    ledgerAccountId: z.uuid(),
})
    .refine((q) => (q.period !== undefined && q.from === undefined && q.to === undefined) ||
    (q.period === undefined && q.from !== undefined && q.to !== undefined), { message: 'Provide either period, or both from and to' });
export const GeneralLedgerAccountSchema = LedgerAccountSchema.pick({
    id: true,
    code: true,
    name: true,
    section: true,
    systemRole: true,
});
export const GeneralLedgerRowSchema = z.object({
    ledgerEntryId: z.uuid(),
    entrySetId: z.uuid(),
    accountingDate: LedgerDateOnlySchema,
    description: z.string(),
    debitMinorUnits: z.number().int().nonnegative(),
    creditMinorUnits: z.number().int().nonnegative(),
    signedAmountMinorUnits: z.number().int(),
    runningBalanceMinorUnits: z.number().int(),
    currency: z.string().length(3),
    clientId: z.uuid().nullable(),
    supplierId: z.uuid().nullable(),
    invoiceId: z.uuid().nullable(),
    supplierInvoiceId: z.uuid().nullable(),
    creditNoteId: z.uuid().nullable(),
    source: GeneralLedgerSourceSchema.nullable(),
});
const GeneralLedgerClientSchema = z.object({
    id: z.uuid(),
    name: z.string(),
});
const GeneralLedgerGroupBaseSchema = z.object({
    groupId: z.string(),
    account: GeneralLedgerAccountSchema,
    naturalSide: GeneralLedgerNaturalSideSchema,
    openingBalanceMinorUnits: z.number().int(),
    rows: z.array(GeneralLedgerRowSchema),
    debitTotalMinorUnits: z.number().int().nonnegative(),
    creditTotalMinorUnits: z.number().int().nonnegative(),
    closingBalanceMinorUnits: z.number().int(),
});
export const GeneralLedgerGroupSchema = z.discriminatedUnion('groupKind', [
    GeneralLedgerGroupBaseSchema.extend({
        groupKind: z.literal('ACCOUNT'),
        client: z.null(),
    }),
    GeneralLedgerGroupBaseSchema.extend({
        groupKind: z.literal('TRUST_CLIENT'),
        client: GeneralLedgerClientSchema,
    }),
]);
export const GeneralLedgerReportSchema = z.object({
    ledgerId: z.uuid(),
    from: LedgerDateOnlySchema,
    to: LedgerDateOnlySchema,
    currency: z.string().length(3),
    accountFilter: z.object({
        ledgerAccountIds: z.array(z.uuid()).nullable(),
    }),
    groups: z.array(GeneralLedgerGroupSchema),
    debitTotalMinorUnits: z.number().int().nonnegative(),
    creditTotalMinorUnits: z.number().int().nonnegative(),
});
// =============================================================================
// VAT Control Report
// =============================================================================
/**
 * The kind of source document a `VAT_CONTROL` leg's entry set carries, read
 * from its sibling legs' open-item document tags (`invoiceId` /
 * `supplierInvoiceId` / `creditNoteId`). `OTHER` covers every untagged set: a
 * manual journal, a direct bank-line allocation, a VAT settlement, an opening
 * balance, or a set posted before tagging existed.
 */
export const VatSourceDocumentTypeSchema = z.enum(['INVOICE', 'CREDIT_NOTE', 'SUPPLIER_INVOICE', 'OTHER']);
/**
 * Which VAT a `VAT_CONTROL` leg belongs to. `UNDETERMINED` means the leg's
 * entry set carries no document tag and its other accounts do not show whether
 * the VAT was charged on a supply or claimed on a purchase (an opening balance,
 * a VAT settlement, a capital purchase, a journal mixing both). It is never
 * folded into either side: whoever files the return declares it.
 */
export const VatControlClassificationSchema = z.enum(['OUTPUT', 'INPUT', 'UNDETERMINED']);
/**
 * A single `VAT_CONTROL` leg in a VAT control report: one `LedgerEntry` posted
 * to the ledger's `VAT_CONTROL`-system-role account, with its entry set's
 * `accountingDate`, `description` (stamped with the source-document reference
 * at posting time) and `intent`. `amountMinorUnits` is debit-positive.
 *
 * `sourceDocumentType` and `classification` come from the rest of the entry
 * set, never from the leg's own sign. A document tag on a sibling leg decides
 * first (credit note, then invoice: OUTPUT; supplier invoice: INPUT). An
 * untagged leg takes its side from the other accounts in the set, and reads
 * `UNDETERMINED` when they do not decide it. The full rule table lives with
 * `deriveVatControlSource` in the backend ledger domain (README § VAT control
 * report). Every rule reads relative direction only, so a reversal, whose legs
 * are its original's negated, always lands on its original's side.
 *
 * `reversedEntrySetId` is non-null exactly when the leg's entry set reverses
 * another.
 */
export const VatControlEntrySchema = z.object({
    ledgerEntryId: z.string(),
    entrySetId: z.string(),
    reversedEntrySetId: z.string().nullable(),
    accountingDate: z.string(),
    amountMinorUnits: z.number().int(),
    classification: VatControlClassificationSchema,
    sourceDocumentType: VatSourceDocumentTypeSchema,
    intent: LedgerIntentSchema,
    description: z.string(),
});
/**
 * VAT control report for a ledger: every `VAT_CONTROL` leg dated on or before
 * `asOf` (and, when `from` is supplied, on or after `from`), ordered by
 * `accountingDate` ascending.
 *
 * `vatControlAccountId` is `null` when the ledger has no `VAT_CONTROL` account
 * (a valid state, e.g. a non-VAT-registered firm) - in that case `entries` is
 * always [].
 *
 * `from` echoes the query input; `null` when omitted (the cumulative,
 * no-lower-bound read - see `VatControlReportQuerySchema` for why the
 * coverage-tracking workflow must keep omitting it).
 */
export const VatControlReportSchema = z.object({
    ledgerId: z.string(),
    asOf: z.string(),
    from: z.string().nullable(),
    vatControlAccountId: z.string().nullable(),
    entries: z.array(VatControlEntrySchema),
});
/**
 * Query params for the VAT-control-entries endpoint. Unlike the other report
 * endpoints, `asOf` is required (no default-to-today): the tax period this
 * report supports is always explicit.
 *
 * `from` is an OPTIONAL lower bound, added for ad-hoc bounded reads (e.g. a
 * manual review of one window). It must NOT be used by the tax-practitioner
 * skill's VAT-return coverage workflow: that workflow relies on the report
 * having no lower bound by default so a late-dated entry (backdated into an
 * already-declared period) still surfaces on a later `asOf` call and gets
 * picked up via the Drive register's `ledgerEntryId` exclusion set (FR5,
 * `requirements-v7.md` in the originating task). Supplying `from` would make
 * any entry dated before it permanently invisible to every future call, which
 * is exactly the failure mode task #990's Codex review caught. See
 * `apps/backend/platform/src/domains/ledger/CLAUDE.md` § "VAT control report".
 */
export const VatControlReportQuerySchema = z.object({
    asOf: LedgerDateOnlySchema,
    from: LedgerDateOnlySchema.optional(),
});
// =============================================================================
// Income Statement
// =============================================================================
/**
 * A single row in an income statement: one ledger account with its
 * natural-positive magnitude over the period. Income sections carry a
 * credit balance (magnitude = -rawSum). Expense sections carry a debit
 * balance (magnitude = rawSum). A refund on an income account produces a
 * negative `balanceMinorUnits` and is retained (not excluded as zero).
 */
export const IncomeStatementAccountSchema = z.object({
    ledgerAccountId: z.string(),
    code: z.string(),
    name: z.string(),
    section: LedgerAccountSectionSchema,
    archivedAt: z.string().nullable(),
    balanceMinorUnits: z.number().int(), // natural-positive magnitude
});
/**
 * One IFRS section group within an income statement. Only sections that have
 * at least one non-zero account are included in `sections[]`.
 */
export const IncomeStatementSectionSchema = z.object({
    section: LedgerAccountSectionSchema,
    label: z.string(), // human label (sentence case), from LEDGER_SECTION_LABELS
    accounts: z.array(IncomeStatementAccountSchema),
    subtotalMinorUnits: z.number().int(), // sum of natural-positive magnitudes
});
/**
 * IFRS-structured income statement (P&L) for a ledger over a date range.
 *
 * `sections[]` contains only sections with at least one non-zero account,
 * ordered by `LEDGER_SECTION_ORDER` (income-statement subset). Account
 * balances are natural-positive magnitudes; zero-balance accounts are excluded.
 *
 * IFRS subtotals:
 *   grossProfitMinorUnits      = Revenue – Cost of sales
 *   operatingProfitMinorUnits  = Gross profit + Other income + Finance income
 *                                – Operating expenses – Depreciation – Other expenses
 *   profitBeforeTaxMinorUnits  = Operating profit – Finance costs
 *   netIncomeMinorUnits        = Profit before tax – Taxation
 *
 * All subtotals can be negative when costs exceed income.
 *
 * `conversion` is non-null when:
 *   - the ledger has an `openingDate`, AND
 *   - the conversion entry set was actually posted (`openingBalancesPosted`), AND
 *   - the requested `from` date is on or before the conversion date (`openingDate − 1`).
 * It carries the conversion boundary so the frontend can render a contextual note.
 * The backend returns only the structured object; rendering copy lives in the UI.
 */
export const IncomeStatementSchema = z.object({
    periodStart: z.string(), // ISO date-time: start of `from` day UTC
    periodEnd: z.string(), // ISO date-time: end of `to` day UTC
    currency: z.string(), // ledger.baseCurrency
    sections: z.array(IncomeStatementSectionSchema),
    grossProfitMinorUnits: z.number().int(),
    operatingProfitMinorUnits: z.number().int(),
    profitBeforeTaxMinorUnits: z.number().int(),
    netIncomeMinorUnits: z.number().int(),
    conversion: z.object({
        conversionDate: z.string(), // YYYY-MM-DD, = openingDate − 1
        preConversionOnly: z.boolean(), // true iff `to` <= conversionDate
    }).nullable(),
});
export const GroupedIncomeStatementNodeSchema = z.lazy(() => z.object({
    id: z.string(),
    name: z.string(),
    sortIndex: z.number().int(),
    subtotalMinorUnits: z.number().int(),
    groups: z.array(GroupedIncomeStatementNodeSchema),
    accounts: z.array(IncomeStatementAccountSchema),
}));
/**
 * Grouped income-statement section: top-level account groups, ungrouped root
 * accounts, and the section's rolled-up subtotal.
 */
export const GroupedIncomeStatementSectionSchema = z.object({
    section: LedgerAccountSectionSchema,
    label: z.string(),
    groups: z.array(GroupedIncomeStatementNodeSchema),
    accounts: z.array(IncomeStatementAccountSchema),
    subtotalMinorUnits: z.number().int(),
});
/**
 * Income statement with presentation-only account groups applied. Figures and
 * computed IFRS subtotals are identical to `IncomeStatementSchema`; only
 * `sections[]` is reshaped.
 */
export const GroupedIncomeStatementSchema = IncomeStatementSchema.extend({
    sections: z.array(GroupedIncomeStatementSectionSchema),
});
/**
 * The grouped income statement as a month-bucketed series - one column per
 * complete calendar month in the resolved report period SET (see "Report
 * period sets" above), plus the financial-year set's year-to-date and
 * optional comparative columns. Every column is a full `GroupedIncomeStatement`
 * for its own window: the series adds no new figures, only more windows of
 * the same shape the single-period endpoint already returns.
 *
 * `fyStartDate` and the four FY-only fields are non-null together, iff
 * `periodSet === 'financial-year'` - mirroring `ResolvedReportPeriodSet`'s
 * discriminated union one level down, since a flat response schema cannot
 * itself discriminate on `periodSet`. `yearToDate` is populated whenever the
 * set is `financial-year`; `priorYearToDate` and `priorFullYear` are
 * additionally gated on the caller having requested `comparatives`.
 *
 * Zero-net accounts are absent per column, exactly as `IncomeStatementSchema`
 * excludes them from a single period - a consumer that wants a stable row set
 * across columns must union account identities across `periods[]` itself.
 */
export const GroupedIncomeStatementSeriesSchema = z.object({
    periodSet: ReportPeriodSetKindSchema,
    /** Latest completed calendar month, `YYYY-MM` - the same bound the query resolved `endingMonth`/`cutoffMonth` against. */
    latestSelectableMonth: YearMonthSchema,
    /** First day of the financial year containing the cutoff, `YYYY-MM-DD`; non-null iff `periodSet === 'financial-year'`. */
    fyStartDate: z.string().nullable(),
    /** One complete calendar month per column, oldest first. 2..12 for `rolling`; 1..12 for `financial-year` (the FY-to-cutoff span). */
    periods: z.array(GroupedIncomeStatementSchema).min(1).max(12),
    /** The financial year's start through the cutoff month; non-null iff `periodSet === 'financial-year'`. */
    yearToDate: GroupedIncomeStatementSchema.nullable(),
    /** The same year-to-date window one financial year back; non-null only when `financial-year` AND `comparatives` were requested. */
    priorYearToDate: GroupedIncomeStatementSchema.nullable(),
    /** The whole prior financial year; non-null only when `financial-year` AND `comparatives` were requested. */
    priorFullYear: GroupedIncomeStatementSchema.nullable(),
});
/**
 * Query params shared by every period report (income statement, cash flow,
 * fixed-asset register). Supply EITHER an explicit `from` AND `to` window OR a
 * single `period` preset, never a mix. The backend resolves a preset to a
 * concrete from/to window using the ledger's financial-year-end month and the
 * UTC calendar date for "today".
 */
const periodReportQueryShape = {
    from: LedgerDateOnlySchema.optional(),
    to: LedgerDateOnlySchema.optional(),
    period: PeriodPresetSchema.optional(),
};
const isPeriodOrFullWindow = (q) => (q.period !== undefined && q.from === undefined && q.to === undefined) ||
    (q.period === undefined && q.from !== undefined && q.to !== undefined);
const PERIOD_OR_WINDOW_MESSAGE = 'Provide either period, or both from and to';
/**
 * Lenient: the income-statement and cash-flow routes that use it are still on the
 * request-schema allowlist. Routes added since use `StrictPeriodReportQuerySchema`.
 */
export const PeriodReportQuerySchema = z
    .object(periodReportQueryShape)
    .refine(isPeriodOrFullWindow, { message: PERIOD_OR_WINDOW_MESSAGE });
/** The same rule, rejecting unknown keys (`request-schemas-strict.test.ts`). */
export const StrictPeriodReportQuerySchema = z
    .strictObject(periodReportQueryShape)
    .refine(isPeriodOrFullWindow, { message: PERIOD_OR_WINDOW_MESSAGE });
/** Query params for the income-statement endpoint: the shared period-report query. */
export const IncomeStatementQuerySchema = PeriodReportQuerySchema;
// =============================================================================
// Balance Sheet
// =============================================================================
/**
 * Query params for the balance-sheet endpoint. Supply EITHER an explicit
 * `asOf` date OR an `asOfPreset`, exactly one of the two. The balance sheet is
 * always calculated as at a specific date, so unlike the trial balance there is
 * no all-time mode. The backend resolves a preset to a concrete date.
 */
export const BalanceSheetQuerySchema = z
    .object({
    asOf: LedgerDateOnlySchema.optional(),
    asOfPreset: AsOfPresetSchema.optional(),
})
    .refine((q) => (q.asOf !== undefined) !== (q.asOfPreset !== undefined), {
    message: 'Provide either asOf or asOfPreset',
});
/**
 * Account balances stay in their stored section, signed in its natural
 * direction: debit-positive for assets, credit-positive for liabilities/equity.
 * Contrary balances remain negative; inactive non-zero accounts remain visible.
 */
export const BalanceSheetAccountSchema = z.object({
    ledgerAccountId: z.string(),
    code: z.string(),
    name: z.string(),
    section: LedgerAccountSectionSchema,
    archivedAt: z.string().nullable(),
    balanceMinorUnits: z.number().int(),
});
/**
 * One section group within a balance sheet (ASSET, LIABILITY, or EQUITY
 * sections). Zero-balance accounts are dropped unconditionally (active or
 * archived), so every account in `accounts[]` is non-zero and a section with
 * no non-zero accounts is omitted from `sections[]` entirely. A non-zero
 * archived account is always retained (accounting-equation carve-out - see
 * "Ledger account archive convention" in `ledger/CLAUDE.md`).
 */
export const BalanceSheetSectionSchema = z.object({
    section: LedgerAccountSectionSchema,
    label: z.string(), // human label (sentence case), from LEDGER_SECTION_LABELS
    accounts: z.array(BalanceSheetAccountSchema),
    subtotalMinorUnits: z.number().int(), // sum of signed account balances
});
/**
 * Balance sheet for a ledger as at a specific date.
 *
 * `sections[]` contains only ASSET, LIABILITY, and EQUITY sections that carry
 * at least one non-zero account, ordered by `LEDGER_SECTION_ORDER`. INCOME
 * and EXPENSE account balances are folded into two derived equity lines:
 *
 *   `accumulatedProfitMinorUnits` – the balance of the ACCUMULATED_PROFIT anchor
 *     account (natural-positive) plus the nominal fold for entries dated before
 *     the start of the current financial year. When `financialYearEndMonth` is
 *     null (legacy ledger), the whole history is treated as the current year, so
 *     this field equals the anchor balance only (0 when no anchor exists).
 *
 *   `currentYearEarningsMinorUnits` – the nominal fold for entries dated within
 *     the current financial year (from the first day after the FYE month through
 *     asOf). When `financialYearEndMonth` is null, this equals the all-time
 *     nominal fold.
 *
 * The ACCUMULATED_PROFIT anchor row is excluded from `sections[]` EQUITY
 * accounts so no figure is double-counted. `accumulatedProfitAccountName` carries
 * that anchor account's own name (e.g. "Retained Earnings", "Owner's Capital
 * Account") for the accumulated-profit line label; null when no anchor exists.
 *
 * `totalEquityMinorUnits` = equity-section subtotals (anchor excluded) +
 *   accumulatedProfitMinorUnits + currentYearEarningsMinorUnits.
 * Accounting equation: totalAssetsMinorUnits === totalLiabilitiesMinorUnits + totalEquityMinorUnits.
 */
export const BalanceSheetSchema = z.object({
    asOf: z.string(), // ISO date-time, end of as-at day UTC
    currency: z.string(),
    sections: z.array(BalanceSheetSectionSchema),
    accumulatedProfitAccountName: z.string().nullable(),
    accumulatedProfitMinorUnits: z.number().int(),
    currentYearEarningsMinorUnits: z.number().int(),
    totalAssetsMinorUnits: z.number().int(),
    totalEquityMinorUnits: z.number().int(),
    totalLiabilitiesMinorUnits: z.number().int(),
});
export const GroupedBalanceSheetNodeSchema = z.lazy(() => z.object({
    id: z.string(),
    name: z.string(),
    sortIndex: z.number().int(),
    subtotalMinorUnits: z.number().int(),
    groups: z.array(GroupedBalanceSheetNodeSchema),
    accounts: z.array(BalanceSheetAccountSchema),
}));
/**
 * Grouped balance-sheet section: top-level account groups, ungrouped root
 * accounts, and the section's rolled-up subtotal.
 */
export const GroupedBalanceSheetSectionSchema = z.object({
    section: LedgerAccountSectionSchema,
    label: z.string(),
    groups: z.array(GroupedBalanceSheetNodeSchema),
    accounts: z.array(BalanceSheetAccountSchema),
    subtotalMinorUnits: z.number().int(),
});
/**
 * Balance sheet with presentation-only account groups applied. Accounting
 * totals and derived equity lines are identical to `BalanceSheetSchema`; only
 * `sections[]` is reshaped.
 */
export const GroupedBalanceSheetSchema = BalanceSheetSchema.extend({
    sections: z.array(GroupedBalanceSheetSectionSchema),
});
/**
 * What an AS-AT series compares its anchor date against. Every option resolves
 * to a set of DATES, which is the whole model: an as-at report states a
 * position as at a date, so a multi-column one is several dates side by side,
 * never a period split into months.
 *
 * `prior-year` is not a special case in the resolver - it is the anchor with
 * one earlier date prepended, exactly like the `previous-N` options.
 *
 * Shared by every as-at report that has a series: the balance sheet, the trial
 * balance, and the trust reconciliation. They compare against the same dates
 * because they answer the same question at the same instant - a trial balance
 * beside a balance sheet that disagreed about what "previous-3" meant would be
 * two reports the reader cannot lay side by side. The FLOW reports (income
 * statement, both cash-flow methods) do NOT use this: their columns are
 * periods, resolved by `resolveReportPeriodSet`.
 */
export const AS_OF_SERIES_COMPARISONS = ['previous-3', 'previous-6', 'previous-12', 'prior-year'];
export const AsOfSeriesComparisonSchema = z.enum(AS_OF_SERIES_COMPARISONS);
/**
 * The grouped balance sheet at several dates at once, oldest first, the anchor
 * date last. Every column is a full `GroupedBalanceSheet` as at its own date:
 * the series adds no new figures, only more dates of the shape the single-date
 * endpoint already returns.
 *
 * There is deliberately no period-set discriminant, no `latestSelectableMonth`
 * and no financial-year envelope. Those belong to a report whose columns are
 * periods (the income statement); this report's columns are dates, and a date
 * needs no period to be meaningful. The anchor is never rounded to a month end,
 * which is what makes "the chosen window contains no completed month"
 * unrepresentable as a failure here: there is always at least one column,
 * because there is always an anchor.
 */
export const GroupedBalanceSheetSeriesSchema = z.object({
    columns: z.array(GroupedBalanceSheetSchema).min(MIN_AS_OF_SERIES_COLUMNS).max(MAX_AS_OF_SERIES_COLUMNS),
});
/**
 * The anchor-plus-comparison shape every as-at series query carries. Kept as a
 * bare shape rather than a schema because each report extends it with its own
 * params before refining (the trial balance adds `showZeroBalances`), and a
 * `.refine()`d schema can no longer be extended.
 */
export const AsOfSeriesQueryShape = {
    asOf: LedgerDateOnlySchema.optional(),
    asOfPreset: AsOfPresetSchema.optional(),
    compare: AsOfSeriesComparisonSchema,
};
/**
 * Exactly one of `asOf` / `asOfPreset`. Shared by every as-at series query so
 * the reports cannot disagree about how an anchor is supplied.
 *
 * `compare` is required and has no "none" member on any of them. A single-
 * column as-at report IS the single-date report, which has its own endpoint and
 * its own renderer - so asking a series endpoint for a lone column is a caller
 * mistake, and the schema says so rather than returning a degenerate series.
 */
export function refineAsOfSeriesAnchor(q) {
    return (q.asOf !== undefined) !== (q.asOfPreset !== undefined);
}
export const AS_OF_SERIES_ANCHOR_MESSAGE = 'Provide either asOf or asOfPreset';
/**
 * The plain as-at series query: an anchor and a comparison, nothing else. Used
 * directly by any as-at series with no report-specific params of its own (the
 * balance sheet, the trust reconciliation).
 */
export const AsOfSeriesQuerySchema = z
    .object(AsOfSeriesQueryShape)
    .refine(refineAsOfSeriesAnchor, { message: AS_OF_SERIES_ANCHOR_MESSAGE });
/**
 * Query params for the balance-sheet series endpoints and their exports: the
 * same anchor `BalanceSheetQuerySchema` takes, plus what to compare it against.
 */
export const BalanceSheetSeriesQuerySchema = z
    .object(AsOfSeriesQueryShape)
    .refine(refineAsOfSeriesAnchor, { message: AS_OF_SERIES_ANCHOR_MESSAGE });
/**
 * Query params for the trial-balance series endpoints and their exports: the
 * as-at series anchor plus the trial balance's own `showZeroBalances`, which
 * means the same thing here as on the single-date report and is applied per
 * column (an account zero in EVERY column is what drops out; see
 * `getGroupedTrialBalanceSeries`).
 */
export const TrialBalanceSeriesQuerySchema = z
    .object({ ...AsOfSeriesQueryShape, showZeroBalances: BooleanQueryParamSchema.optional() })
    .refine(refineAsOfSeriesAnchor, { message: AS_OF_SERIES_ANCHOR_MESSAGE });
/** How many earlier month-end columns each comparison puts before the anchor. */
export function asOfSeriesMonthsBack(compare) {
    switch (compare) {
        case 'previous-3':
            return 3;
        case 'previous-6':
            return 6;
        case 'previous-12':
            return 12;
        case 'prior-year':
            // Not a month-end walk at all - one column, the anchor's own date a
            // year earlier. Resolved separately; see `asOfSeriesDates`.
            return 0;
    }
}
// =============================================================================
// Cash Flow Statement
// =============================================================================
/**
 * A single line within a cash flow activity group. One line per contra ledger
 * account (direct method, §7.10). `amountMinorUnits` is signed:
 * positive = cash inflow, negative = cash outflow.
 */
export const CashFlowLineSchema = z.object({
    ledgerAccountId: z.string().nullable(),
    code: z.string().nullable(),
    name: z.string(),
    section: LedgerAccountSectionSchema.nullable(),
    amountMinorUnits: z.number().int(), // signed; inflow positive
});
/**
 * One of the three activity groups in a cash flow statement (operating,
 * investing, or financing). `subtotalMinorUnits` is the net cash movement
 * for the activity over the period. Groups with no movements carry an empty
 * `lines` array and a zero `subtotalMinorUnits`.
 */
export const CashFlowActivityGroupSchema = z.object({
    activity: CashFlowActivitySchema,
    label: z.string(), // from CASH_FLOW_ACTIVITY_LABELS
    lines: z.array(CashFlowLineSchema),
    subtotalMinorUnits: z.number().int(),
});
export const CASH_BOUNDARY_ROLES = Object.freeze([
    'BANK',
    'TRANSFER_CLEARING',
]);
/**
 * Whether `systemRole` sits ON the cash boundary - the accounts whose balance
 * IS the cash figure both cash flow statements open and close on, and which
 * therefore never appear as a movement on either statement.
 *
 * The predicate form, not the constant, is what every consumer wants: a leg's
 * or a balance row's `systemRole` is nullable, so membership testing against
 * `CASH_BOUNDARY_ROLES` directly invites a cast. Keeping the null case here
 * means no consumer can express the boundary as its own literal list, which
 * is how the direct series came to classify by a `switch` whose cases nothing
 * kept in step with the constant.
 *
 * `ledgerFindCashFlowCashBoundaryTotals.sql` restates the list because
 * TypedSQL cannot reference a TS constant; it is pinned against
 * `CASH_BOUNDARY_ROLES` by `ledger.repository.cash-flow.test.ts`.
 */
export function isCashBoundaryRole(systemRole) {
    if (systemRole === null)
        return false;
    return CASH_BOUNDARY_ROLES.includes(systemRole);
}
export const CASH_FLOW_RECONCILING_ROLE_LABELS = Object.freeze({
    BANK_SUSPENSE: 'Uncategorised bank movements',
    PAYMENTS_CLEARING: 'Payments awaiting bank confirmation',
});
/**
 * Whether both cash flow statements classify an account by its SYSTEM ROLE
 * before they ever read its `CashFlowTreatment` - the cash-boundary roles
 * (`CASH_BOUNDARY_ROLES`) and the reconciling roles
 * (`CASH_FLOW_RECONCILING_ROLE_LABELS`).
 *
 * Both projections branch on the role first and never reach the account's
 * classification for these four:
 *   - direct method: the cash boundary is the statement's own subject and
 *     never displays as a line, and a reconciling role only ever surfaces as
 *     a synthetic residual carrying the role's label under OPERATING;
 *   - indirect method: the movement pass skips a cash-boundary row outright
 *     and turns a reconciling row into that same synthetic line, the §7.8(a)
 *     gross-up excludes both from its counterpart legs, and the §7.8(c)
 *     reclassification pass skips both.
 *
 * So a treatment recorded on one of these accounts is inert in the same way an
 * `OPERATING_*` value on a nominal account is - it saves, and no number in
 * either statement moves. `isCashFlowTreatmentPermittedForAccount` withholds
 * every treatment on a role-classified account for that reason.
 */
export function isCashFlowRoleClassified(systemRole) {
    if (systemRole === null)
        return false;
    return isCashBoundaryRole(systemRole) || systemRole in CASH_FLOW_RECONCILING_ROLE_LABELS;
}
/**
 * Roles a cashbook movement's classification never surfaces as an allocation
 * leg: `isCashFlowRoleClassified`'s four (the cash boundary and the
 * reconciling roles) plus `VAT_CONTROL`. Shared between the cashbook's
 * backend query guard (`isCashbookNonClassificationRole`,
 * `cashbook.helpers.ts`, which delegates here) and its account-filter picker
 * (`CashbookPage.tsx`), so the two cannot drift the way the picker and the
 * 400 guard did before this existed - the picker offered six plumbing
 * accounts the guard already rejected.
 *
 * `VAT_CONTROL` is on top of the cash-flow set for the same reason
 * `isCashFlowRoleClassified`'s own doc comment gives for the other four: the
 * cashbook surfaces VAT separately as `vatMinorUnits`, so a VAT Control leg
 * must not also appear as an allocation leg.
 */
export function isCashbookNonClassificationRole(systemRole) {
    if (systemRole === null)
        return false;
    return isCashFlowRoleClassified(systemRole) || systemRole === 'VAT_CONTROL';
}
/**
 * Direct-method cash flow statement for a ledger over a date range.
 *
 * `activities` always contains exactly three groups in `CASH_FLOW_ACTIVITY_ORDER`
 * (OPERATING, INVESTING, FINANCING). Groups with no movements carry an empty
 * `lines` array and a zero `subtotalMinorUnits`.
 *
 * Reconciliation invariant (§7.20):
 *   netChangeInCashMinorUnits === closingCashMinorUnits - openingCashMinorUnits
 *                              === sum(activities[].subtotalMinorUnits)
 */
export const CashFlowSchema = z.object({
    periodStart: z.string(), // ISO date-time: start of `from` day UTC
    periodEnd: z.string(), // ISO date-time: end of `to` day UTC
    currency: z.string(), // ledger.baseCurrency
    activities: z.array(CashFlowActivityGroupSchema),
    netChangeInCashMinorUnits: z.number().int(),
    openingCashMinorUnits: z.number().int(),
    closingCashMinorUnits: z.number().int(),
});
// =============================================================================
// Indirect Cash Flow Statement
// =============================================================================
export const IndirectCashFlowLineSchema = CashFlowLineSchema.extend({
    archivedAt: z.string().nullable(),
});
export const IndirectCashFlowActivityGroupSchema = CashFlowActivityGroupSchema.extend({
    lines: z.array(IndirectCashFlowLineSchema),
});
export const IndirectCashFlowSchema = z.object({
    periodStart: z.string(),
    periodEnd: z.string(),
    currency: z.string(),
    operating: z.object({
        netProfitMinorUnits: z.number().int(),
        nonCashAdjustments: z.array(IndirectCashFlowLineSchema),
        reclassifications: z.array(IndirectCashFlowLineSchema),
        workingCapitalChanges: z.array(IndirectCashFlowLineSchema),
        interestMovements: z.array(IndirectCashFlowLineSchema),
        taxMovements: z.array(IndirectCashFlowLineSchema),
        reconcilingItems: z.array(IndirectCashFlowLineSchema),
        subtotalMinorUnits: z.number().int(),
    }),
    investing: IndirectCashFlowActivityGroupSchema,
    financing: IndirectCashFlowActivityGroupSchema,
    netChangeInCashMinorUnits: z.number().int(),
    openingCashMinorUnits: z.number().int(),
    closingCashMinorUnits: z.number().int(),
});
/**
 * The line blocks inside the indirect statement's operating section, in the
 * order every renderer presents them. The single source of both the order and
 * the headings: the web statement, the PDF, the XLSX and the monthly series
 * each iterate this table, and the projection sums it to reach the operating
 * subtotal, so a block that is not here is presented nowhere and counted
 * nowhere.
 *
 * No block carries a subtotal of its own. The one operating subtotal spans net
 * profit and every block together, which is why a line sitting in the wrong
 * block still reconciles to the cash boundary - the headings are assertions the
 * projection makes, and only per-block tests can check them.
 *
 * The order follows IFRS for SMEs §7.8: the (a) non-cash adjustments, then the
 * (c) reclassifications out of operating, then the (a) working capital changes,
 * then the §7.14 interest and dividend and §7.17 income tax movements each
 * presented separately. `Reconciling items` closes the section; it is a Bill
 * artefact - money the bank has seen that the ledger has not yet classified -
 * with no IFRS analogue.
 *
 * Every heading names the ADJUSTMENT the block makes, never the IFRS
 * disclosure it serves. The §7.14 and §7.17 disclosures are about CASH -
 * interest paid and received, taxes paid - but what lands in these two blocks
 * is the period movement on a balance-sheet account somebody marked by hand.
 * On a ledger accruing R2,750.00 of interest and paying R1,900.00 of it, the
 * interest block reads R850.00, so a heading of "Interest and dividends"
 * would be read as interest paid and be wrong by the whole accrual. `Changes
 * in ...` says what the number is.
 *
 * The interest block names its two streams separately, and §7.14 is why it
 * has to. The paragraph requires interest and dividends received and paid to
 * be presented separately from EACH OTHER, not merely from other cash flows -
 * §7.15 and §7.16 let interest paid, interest received, dividends received and
 * dividends paid land in three different activities within one statement,
 * which they could not do if they were one class. The block is compliant at
 * line level, because each marked account renders as its own row inside it, so
 * only the heading could breach the paragraph. `Changes in interest and
 * dividend balances` did: a single `... and ...` compound names the two as one
 * class, which is the one thing §7.14 denies. Repeating `balances` costs a
 * word and names two movements, and holds whether or not a seventh treatment
 * ever splits the block in two.
 *
 * `Items removed to investing or financing` follows the same rule, and the
 * sign is why it has to. The block holds the NEGATED operating leg of the
 * §7.8(c) pair: a R50.00 disposal gain sits here as -R50.00 while +R50.00
 * appears under investing. A heading naming what the items ARE - "Items
 * classified as investing or financing" - leaves a bookkeeper reading a
 * negative figure with nothing on the face of the statement saying it is a
 * removal. `removed to` names the adjustment and its destination, so the
 * minus sign explains itself.
 */
export const INDIRECT_CASH_FLOW_OPERATING_BLOCKS = [
    { label: 'Non-cash adjustments', key: 'nonCashAdjustments' },
    { label: 'Items removed to investing or financing', key: 'reclassifications' },
    { label: 'Changes in working capital', key: 'workingCapitalChanges' },
    { label: 'Changes in interest balances and dividend balances', key: 'interestMovements' },
    { label: 'Changes in income tax balances', key: 'taxMovements' },
    { label: 'Reconciling items', key: 'reconcilingItems' },
];
/**
 * Which block of the indirect statement's operating section a
 * `CashFlowTreatment` lands a balance-sheet movement in. `INVESTING` and
 * `FINANCING` are not blocks of the operating section at all - they are the
 * statement's own flat activity sections - so they carry `null`.
 *
 * The single source of the treatment-to-block bridge, and exhaustive over
 * `CashFlowTreatment` so a seventh treatment does not compile until it says
 * where it presents. `satisfies` constrains every value to a real key of
 * `IndirectCashFlow['operating']`, so a block that is presented nowhere cannot
 * be named here.
 *
 * Only the KEYS live here. The headings are read off
 * `INDIRECT_CASH_FLOW_OPERATING_BLOCKS`, which owns both the order and the
 * wording every renderer presents.
 *
 * The projection reads this map for the four operating treatments and routes
 * the other two to its own investing and financing line lists; the ledger CLI
 * reads it to name, in a plan, the block an account will present under. The
 * two blocks no treatment reaches - `reclassifications` and
 * `reconcilingItems` - are produced by passes of their own rather than by an
 * account's classification.
 */
export const CASH_FLOW_TREATMENT_TO_OPERATING_BLOCK = Object.freeze({
    OPERATING_WORKING_CAPITAL: 'workingCapitalChanges',
    OPERATING_NON_CASH: 'nonCashAdjustments',
    OPERATING_TAX: 'taxMovements',
    OPERATING_INTEREST: 'interestMovements',
    INVESTING: null,
    FINANCING: null,
});
/**
 * Cash-flow monthly series: one canonical `CashFlow` per complete calendar
 * month in the resolved period set. Unlike the income statement, cash flow
 * does not currently render prior-year comparative columns; callers asking
 * for a financial-year set receive only its month run.
 */
export const CashFlowSeriesSchema = z.object({
    periodSet: ReportPeriodSetKindSchema,
    latestSelectableMonth: YearMonthSchema,
    fyStartDate: z.string().nullable(),
    periods: z.array(CashFlowSchema).min(1).max(12),
});
/** Query params for the cash-flow endpoint: the shared period-report query. */
export const CashFlowQuerySchema = PeriodReportQuerySchema;
export const IndirectCashFlowSeriesSchema = z.object({
    periodSet: ReportPeriodSetKindSchema,
    latestSelectableMonth: YearMonthSchema,
    fyStartDate: z.string().nullable(),
    periods: z.array(IndirectCashFlowSchema).min(1).max(12),
});
export const CashFlowSeriesQuerySchema = ReportPeriodSetQuerySchema.refine((query) => query.comparatives !== true, { error: 'Cash flow series does not support comparatives' });
// =============================================================================
// Age Analysis (Debtors / Creditors)
// =============================================================================
/**
 * Query params for both age-analysis endpoints (debtors, creditors). Supply
 * EITHER an explicit `asOf` date OR an `asOfPreset`, exactly one of the two -
 * mirrors `BalanceSheetQuerySchema` (an age analysis is always as at a single
 * date, like a balance sheet, never a period).
 */
export const AgeAnalysisQuerySchema = z
    .object({
    asOf: LedgerDateOnlySchema.optional(),
    asOfPreset: AsOfPresetSchema.optional(),
})
    .refine((q) => (q.asOf !== undefined) !== (q.asOfPreset !== undefined), {
    message: 'Provide either asOf or asOfPreset',
});
/**
 * One party's row in an age analysis: the accumulated bucket vector for every
 * open document belonging to that party (client for debtors, supplier for
 * creditors). `bucketsMinorUnits` is aligned to the parent `AgeAnalysisSchema`'s
 * `bucketLabels` order; an entry may be negative (an over-credited / over-settled
 * document lands in the Current bucket, negative). `totalMinorUnits` is the sum
 * of `bucketsMinorUnits` and may itself be negative for a party in overall credit.
 */
export const AgeAnalysisRowSchema = z.object({
    partyId: z.string(),
    partyName: z.string(),
    bucketsMinorUnits: z.array(z.number().int()),
    // Balances tagged to this party but to no document: a receipt reconciled
    // straight to the party, a credit on account, an opening retag. They have a
    // counterparty but no document date, so they are attributable and not
    // ageable - they sit outside `bucketsMinorUnits` and inside
    // `totalMinorUnits`. Showing them against the party is what keeps a party
    // in credit from reading as owing the gross of their open documents.
    unallocatedMinorUnits: z.number().int(),
    totalMinorUnits: z.number().int(),
});
/**
 * Debtors or creditors age analysis for a ledger as at a resolved date. Each
 * document's outstanding is read directly from its own tagged legs on the
 * ledger's AR/AP control account as at `asOf` (see
 * `financial-reports/age-analysis/age-analysis.service.ts` - no legacy settlement union,
 * no credit-note term: a credit-note application posts a tagged Cr AR leg,
 * so the tagged net already includes it). Rows with a zero net total are
 * omitted; `rows` is ordered by `totalMinorUnits` descending.
 * `bucketTotalsMinorUnits` and `grandTotalMinorUnits` are column/grand sums
 * over the emitted `rows`, so the arithmetic invariant (a row's bucket
 * amounts sum to its total; row totals sum to the grand total) holds by
 * construction.
 *
 * The report ties to the balance sheet BY CONSTRUCTION via a third,
 * independent invariant:
 *
 *     grandTotalMinorUnits + unattributedMinorUnits === controlTotalMinorUnits
 *
 * `unattributedMinorUnits` is a SUBTRACTION (`controlTotal - grandTotal`),
 * never an estimate, so whatever the report cannot attribute to a document -
 * a pre-tagging leg, a mixed-era document, an opening-retag leg carrying a
 * party tag but no document tag, a manual AR/AP journal, or the legs of a
 * hard-deleted document - lands here automatically and renders as a visible
 * reconciling line. A silent under-report is structurally impossible.
 */
export const AgeAnalysisSchema = z.object({
    asOf: z.string(), // resolved YYYY-MM-DD echo
    currency: z.string().length(3),
    bucketLabels: z.array(z.string()), // ['Current', '1–30 days', ..., '120+ days']
    rows: z.array(AgeAnalysisRowSchema),
    bucketTotalsMinorUnits: z.array(z.number().int()),
    grandTotalMinorUnits: z.number().int(),
    // AR/AP control account balance at `asOf` - the same figure the balance
    // sheet shows for this control account. Debtors: the raw ledger read
    // (AR is already natural-positive when outstanding). Creditors: negated
    // by `financial-reports` from the raw debit-positive ledger read, so this
    // is the natural-positive "amount owed" figure, matching the balance
    // sheet's liability presentation.
    controlTotalMinorUnits: z.number().int(),
    // controlTotalMinorUnits - grandTotalMinorUnits: the control-account
    // balance carrying no document tag the report could bucket. See the
    // tie invariant above.
    unattributedMinorUnits: z.number().int(),
});
// =============================================================================
// Ledger input schemas (create / patch / entry-set mutations)
// =============================================================================
/**
 * Opening date constrained to the 1st of a month. Ledger-specific refinement;
 * `CalendarDateSchema` itself is left unconstrained for other consumers
 * (invoices, activities, report ranges).
 */
export const LedgerOpeningDateSchema = CalendarDateSchema.refine((d) => typeof d === 'string' && d.endsWith('-01'), 'Opening date must be the 1st of a month');
export const CreateLedgerInputSchema = z.object({
    baseCurrency: z.string().length(3).optional(),
    // The opening date: all historical opening balances are brought in as of
    // this date. Must be the 1st of a month. Required at ledger creation across every path.
    openingDate: LedgerOpeningDateSchema,
    financialYearEndMonth: z.number().int().min(1).max(12).nullable().optional(),
    vatFilingCategory: VatFilingCategorySchema.nullable().optional(),
    vatNumber: saVatNumberSchema,
    // The "with effect from" date on the VAT registration notice; written to the
    // account's business entity like vatNumber.
    vatRegisteredFrom: CalendarDateOnlySchema.nullable().optional(),
});
// Partial update of ledger settings. All fields are optional so a caller can
// patch only financialYearEndMonth or vatFilingCategory without supplying an
// openingDate. The opening date is editable until opening balances are posted,
// after which the server locks it. vatNumber is written to the account's
// business entity (same path as createLedger).
export const PatchLedgerInputSchema = z.object({
    openingDate: LedgerOpeningDateSchema.optional(),
    financialYearEndMonth: z.number().int().min(1).max(12).nullable().optional(),
    vatFilingCategory: VatFilingCategorySchema.nullable().optional(),
    vatNumber: saVatNumberSchema,
});
// Create a plain ledger account. There is deliberately no `bankAccountId`
// field here: a public create that also links a bank account left the
// linked row without its paired clearing account provisioned (clearing is
// a second GL row, not a by-product of naming a section/role), so
// `resolveClearingForBankAccount` later failed for a bank the API reported
// as linked. Linking a bank account to a ledger account is a distinct
// operation - `linkBankAccountToLedgerAccount` (via
// `LinkBankAccountToLedgerAccountInputSchema`) - that provisions both the
// bank-role row and its clearing row together. No `.strict()`: a stale
// caller that still sends `bankAccountId` has it silently stripped by Zod
// rather than rejected, mirroring `PatchLedgerAccountInputSchema` below.
export const CreateLedgerAccountInputSchema = z.object({
    name: z.string().min(1).max(255),
    section: LedgerAccountSectionSchema,
    defaultVatTreatment: VatTreatmentSchema.nullable().optional(),
});
// Partial update of a ledger account. All fields are optional so a caller can
// rename, toggle activation, or reassign a system role independently. The
// service rejects an empty patch. `name` rename is refused for system-role
// accounts (see `updateLedgerAccount`), whose names are the canonical
// vocabulary server-side reports rely on. There is deliberately no
// `bankAccountId` field here: linking a ledger account to a bank account also
// provisions the bank's paired clearing account, which this generic PATCH
// must not do as a hidden side-effect (`api/CLAUDE.md` § "Prefer Dumb,
// Single-Purpose Endpoints"). Use `LinkBankAccountToLedgerAccountInputSchema`
// via `linkBankAccountToLedgerAccount` instead. No `.strict()`: a stale caller
// that still sends `bankAccountId` has it silently stripped by Zod rather
// than rejected, since every UI and e2e caller was migrated off this field
// before it was removed.
export const PatchLedgerAccountInputSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    systemRole: LedgerAccountSystemRoleSchema.nullable().optional(),
    defaultVatTreatment: VatTreatmentSchema.nullable().optional(),
    // Reclassify the account into a different section, subject to two service-layer
    // guards: the move must stay within the same account type or between asset and
    // liability sections, and a role-pinned account (e.g. ACCOUNTS_RECEIVABLE) must
    // stay in its required section. The account's `code` never changes on a section
    // move. Rejected in the same patch as `systemRole` - change the section and the
    // role in separate requests.
    section: LedgerAccountSectionSchema.optional(),
    // Record the cash flow treatment this account's flows are presented under, or
    // send null to clear it and go back to deriving the treatment from `section`.
    // Any value is accepted on any account: the write classifies a fact that has
    // already occurred, so it is never a posting gate
    // (`.claude/rules/record-what-happened.md`).
    cashFlowTreatment: CashFlowTreatmentSchema.nullable().optional(),
});
// Explicit bank-account link: the caller names both the bank account and the
// bank-role ledger account it should claim. Used when the auto-adoption
// listener deferred (2+ unlinked bank-role candidates in the ledger) and an
// operator must choose which row is the real bank - see
// `linkBankAccountToChosenLedgerAccount` (ledger domain).
export const LinkBankAccountToLedgerAccountInputSchema = z.object({
    bankAccountId: z.string().uuid(),
});
// Body for POST .../bank-accounts/:bankAccountId/ledger-link
// (provisionBankScopedLedgerAccounts) - provisions a FRESH bank-role ledger
// account (plus its paired clearing and suspense rows) for a single bank
// account and links it. A resource-creating POST (up to three LedgerAccount
// rows), so `idempotencyKey` is required per `api/CLAUDE.md` "Idempotency key
// on POST endpoints" - it dedupes a replayed identical request (same key
// reused); see the service function's own doc comment for what protects a
// genuinely concurrent double-submit instead (a P2002 -> 409 mapping on the
// bank-role leg's create).
export const ProvisionBankScopedLedgerAccountsInputSchema = z.object({
    idempotencyKey: z.string().min(1).max(255),
});
// Querystring for DELETE .../accounts/:ledgerAccountId (archiveLedgerAccount).
// `allowNonZeroBalance=true` opts into the super-admin-gated override that
// relaxes the non-zero-balance archive guard (bank-linked and last-active-
// system-role guards still apply unconditionally). String-enum, not
// `z.coerce.boolean()`, mirroring `ListSuppliersQuerySchema.includeArchived` -
// the route dispatches on `=== 'true'`.
export const ArchiveLedgerAccountQuerySchema = z.object({
    allowNonZeroBalance: z.enum(['true', 'false']).optional(),
});
export const CreateLedgerEntrySetInputSchema = z.object({
    accountingDate: z.string().datetime(),
    description: z.string().min(3).max(2000),
    idempotencyKey: z.string().min(1).max(255),
    entries: z.array(z.object({
        ledgerAccountId: z.string().uuid(),
        amountMinorUnits: z.number().int().min(-9_999_999_999_999).max(9_999_999_999_999),
        currency: z.string().length(3),
        // Optional party tags for the leg (e.g. tagging an AR/trust-creditors
        // entry to the client it belongs to, or an AP entry to the supplier).
        // A leg may carry clientId OR supplierId, never both. The HTTP framework
        // reparses ajv-accepted input through Zod, so a refinement here would run
        // for wire callers. The service remains the authoritative enforcement
        // site because direct callers must obey the same invariant.
        clientId: z.string().uuid().nullable().optional(),
        supplierId: z.string().uuid().nullable().optional(),
        // Attributes the leg to one of its client's matters; null is client level.
        // `createLedgerEntrySet` refuses a matter outside the leg's client.
        clientProjectId: z.string().uuid().nullable().optional(),
    })).min(2).max(500),
});
export const ReverseLedgerEntrySetInputSchema = z.object({
    accountingDate: z.string().datetime().optional(),
    description: z.string().max(2000).optional(),
});
/** One trust bank account's opening balance, in minor units. */
export const TrustBankLegSchema = z.object({
    bankAccountId: LowercaseUuidSchema,
    balanceMinorUnits: z.number().int().min(1),
});
/**
 * One-shot import of a trust ledger's entire opening balance sheet: one debit
 * per trust bank leg, N per-client credits to the trust creditors control
 * account, and an optional credit to the LPFF interest payable control account.
 * All amounts are minor units (matches the platform's monetary-boundary
 * convention; the frontend converts entered decimals).
 *
 * Each `trustBankLegs` row must name a bank account already linked to this
 * ledger as TRUST_BANK_S86_2, TRUST_BANK_S86_3 or TRUST_BANK_S86_4.
 * `interestPayableBalanceMinorUnits` defaults to 0 when the ledger carries no
 * LPFF interest liability yet, and may be negative for a mid-cycle net debit.
 *
 * `unknownOwnerBalanceMinorUnits` carries no equivalent sign: it is money the
 * firm holds in trust for a beneficiary who has not yet been identified
 * (`TRUST_UNKNOWN_OWNER`), a liability the same way `TRUST_CREDITORS` is, and a
 * debit balance on a trust creditor is a trust shortfall the Legal Practice
 * Council does not allow (Rule 54.14.9; see `assertClientChargeStaysInCredit`
 * in `bank-statement-line.service.classify-trust.ts` for where the same rule is
 * enforced on a named client's trust creditor leg). Non-negative only.
 *
 * `clientBalances` may be empty - a firm whose trust money is all unattributed
 * at the opening date has no client rows to state, and the tie-out below is
 * what keeps such an import honest. A duplicate `clientId`, duplicate bank
 * account leg, or a total where `sum(trustBankLegs)` does not equal
 * `sum(clientBalances) + interestPayableBalanceMinorUnits + unknownOwnerBalanceMinorUnits`
 * is rejected by the service.
 * Negative client balances are trust deficits: the write succeeds and returns
 * warnings naming the affected clients.
 */
export const TrustOpeningBalancesImportRequestSchema = z.object({
    // The 500 ceiling mirrors `clientBalances` for symmetry; it is not a
    // considered concurrency bound. What actually bounds this array is the
    // number of trust bank accounts the firm has linked to this ledger - a
    // handful, since each is a real bank account (one or two s 86(2) cheque
    // accounts, the practice's own s 86(3) investment account, plus one s 86(4)
    // investment account per investing client). Do not size infrastructure
    // against 500.
    trustBankLegs: z.array(TrustBankLegSchema).min(1).max(500),
    interestPayableBalanceMinorUnits: z.number().int().default(0),
    // No `.safe()`: it emits its own `minimum` of `Number.MIN_SAFE_INTEGER`,
    // which overwrites the one `.min(0)` produced in the generated JSON schema
    // that ajv actually enforces on the request body - so the wire contract
    // would admit negatives while only Zod's own parse rejected them. `.int()`
    // already caps at `Number.MAX_SAFE_INTEGER`, so `.safe()` bought nothing.
    // Pinned by a test in `tests/ledger.test.ts` over the draft-07 output.
    //
    // `.default(0)` rather than `.optional()`, matching
    // `interestPayableBalanceMinorUnits` above: these are the two non-client trust
    // liabilities the bank legs must tie to, so they are one logical group and a
    // caller reads them together. The field stays omissible on the wire either
    // way; what the default buys is that every reader gets a `number` instead of
    // spreading `?? 0` across the tie-out, the guard and the posting gate.
    unknownOwnerBalanceMinorUnits: z.number().int().min(0).default(0),
    clientBalances: z.array(z.object({
        clientId: LowercaseUuidSchema,
        balanceMinorUnits: z.number().int(),
    })).max(500),
    idempotencyKey: z.string().min(1).max(255),
});
/**
 * Flat: the posted entry set's own fields plus `warnings`, rather than a
 * `{ entrySet, warnings }` wrapper. Zod object schemas strip unknown keys
 * unless `.strict()` is set (neither this schema nor `LedgerEntrySetSchema`
 * sets `.strict()`/`.passthrough()`), so a client bundle built before
 * `warnings` was added still parses this response successfully against its
 * old `LedgerEntrySetSchema` - it just ignores the extra key. That backwards
 * compatibility is pinned by a test in `tests/ledger.test.ts` asserting the
 * pre-existing schema still accepts the flattened shape.
 */
export const TrustOpeningBalancesImportResponseSchema = LedgerEntrySetSchema.extend({
    warnings: z.array(z.string()),
});
// =============================================================================
// Chart of Accounts Presets
// =============================================================================
export const ChartOfAccountsPresetVisibilitySchema = z.enum(['public', 'admin-only']);
export const ChartOfAccountsPresetAccountSchema = z.object({
    name: z.string(),
    section: LedgerAccountSectionSchema,
    systemRole: LedgerAccountSystemRoleSchema.nullable(),
    linkBankAccount: z.boolean(),
    // Per-account VAT treatment the preset stamps onto the created account.
    // Null = unset (gross-post); surfaced so the admin UI can preview what each
    // preset account will carry before applying.
    defaultVatTreatment: VatTreatmentSchema.nullable(),
});
export const ChartOfAccountsPresetSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    description: z.string(),
    // The ledger kind this preset targets. Consumers filter by kind to determine
    // which presets to offer: the onboarding picker grid shows only BUSINESS
    // presets (the toggle's trust side is the sole trust-seeding path, collecting
    // an opening date only); the provision-account admin pages also filter to
    // BUSINESS. A TRUST preset seeds every row unlinked; the trust bank is created
    // separately and adopted asynchronously by the bank-account.created listener.
    kind: LedgerKindSchema,
    visibility: ChartOfAccountsPresetVisibilitySchema,
    accounts: z.array(ChartOfAccountsPresetAccountSchema),
});
export const ChartOfAccountsPresetListResponseSchema = z.object({
    data: z.array(ChartOfAccountsPresetSchema),
});
export const ApplyChartOfAccountsPresetInputSchema = z.object({
    presetId: z.string().min(1),
});
// Used by the seed-chart-of-accounts route which creates the ledger and applies
// the preset in one call. The caller supplies openingDate here because the ledger
// does not exist yet; the apply-preset-only route omits it (the ledger already
// owns its date).
//
// financialYearEndMonth, vatFilingCategory, and vatNumber are persisted whether
// the ledger is created or already exists. VAT accounts are NOT injected here
// because the preset already includes them; injecting would cause a conflict.
//
// No trust-bank-account field: trust banks are set up in isolation. The trust
// preset seeds every row unlinked; a `kind:'TRUST'` bank account (created only
// once this trust ledger exists) is adopted afterwards by the
// `bank-account.created` listener, mirroring how a business bank account is
// linked.
export const SeedChartOfAccountsInputSchema = z.object({
    presetId: z.string().min(1),
    openingDate: LedgerOpeningDateSchema,
    financialYearEndMonth: z.number().int().min(1).max(12).nullable().optional(),
    vatFilingCategory: VatFilingCategorySchema.nullable().optional(),
    vatNumber: saVatNumberSchema,
});
/** Seed result plus conduct warnings from the VAT settings written with it. */
export const SeedChartOfAccountsResponseSchema = LedgerAccountListResponseSchema.extend(writeWarningsFields);
// =============================================================================
// Account Groups
//
// Presentation-only grouping tree over a ledger's chart of accounts. Mirrors
// the `LedgerAccountGroup` Prisma model (adjacency list: `parentId = null` is
// top-level within its `section` - the 13 section roots stay VIRTUAL, never
// materialised as rows). Groups carry no journal, no balance, no history -
// disposable metadata over `LedgerAccount` rows, which stay real
// (archive-only). See
// `.asdf/tasks/.../198_hierarchical-account-group-presentation/design.md` for
// the full design and the composite-FK alternative rejected in favour of
// service-layer invariants (section/ledger consistency, no-cycles, max depth).
// =============================================================================
export const LedgerAccountGroupSchema = z.object({
    id: z.string(),
    ledgerId: z.string(),
    accountId: z.string(),
    parentId: z.string().nullable(),
    section: LedgerAccountSectionSchema,
    name: z.string(),
    sortIndex: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// Input === Output (a response schema over already-shaped JSON): the second
// type arg is required so the whole GroupedTrialBalanceSchema satisfies the
// `api()` client helper's invariant `ZodType<T, T>` constraint (input=output).
export const GroupedTrialBalanceNodeSchema = z.lazy(() => z.object({
    id: z.string(),
    name: z.string(),
    sortIndex: z.number().int(),
    subtotalMinorUnits: z.number().int(),
    groups: z.array(GroupedTrialBalanceNodeSchema),
    accounts: z.array(TrialBalanceAccountSchema),
}));
/**
 * One `LedgerAccountSection` root in a grouped trial balance: its top-level
 * groups (ordered by `sortIndex`), its ungrouped direct accounts (ordered by
 * `code`), and the section's rolled-up subtotal.
 */
export const GroupedTrialBalanceSectionSchema = z.object({
    section: LedgerAccountSectionSchema,
    label: z.string(), // human label (sentence case), from LEDGER_SECTION_LABELS
    groups: z.array(GroupedTrialBalanceNodeSchema),
    accounts: z.array(TrialBalanceAccountSchema),
    subtotalMinorUnits: z.number().int(),
});
/**
 * v1 grouped presentation of a ledger's trial balance: every
 * `LedgerAccountSection` in `LEDGER_SECTION_ORDER` that carries at least one
 * visible group or account, each expanded into its nested group tree. Shares
 * `asOf` / `fyStartDate` / `currency` / debit-credit totals with
 * `TrialBalanceSchema` since this is a reshaping of the same underlying trial
 * balance (`getTrialBalance` + the flat `LedgerAccountGroup` rows), not a
 * separate figure - the two must always net to the same totals.
 */
export const GroupedTrialBalanceSchema = z.object({
    asOf: z.string().nullable(),
    fyStartDate: z.string().nullable(),
    sections: z.array(GroupedTrialBalanceSectionSchema),
    currency: z.string().length(3),
    debitTotalMinorUnits: z.number().int().nonnegative(),
    creditTotalMinorUnits: z.number().int().nonnegative(),
});
/**
 * Create a new group. `parentId = null` creates a top-level group directly
 * under the section root; a non-null `parentId` nests it under an existing
 * group (which must belong to the same `ledgerId` and `section` - enforced by
 * the service, not the DB - see the module doc comment above). Resource-
 * creating POST, so `idempotencyKey` is required.
 */
export const CreateAccountGroupInputSchema = z.object({
    section: LedgerAccountSectionSchema,
    parentId: z.string().uuid().nullable(),
    name: z.string().min(1).max(255),
    idempotencyKey: z.string().min(1).max(255),
});
export const RenameAccountGroupInputSchema = z.object({
    name: z.string().min(1).max(255),
});
/**
 * Reparent a group. `parentId = null` moves it to top-level within its own
 * (unchanged) section. The service rejects a parent in a different ledger or
 * section, a cycle (ancestor-walk), and a resulting depth beyond the ~5-level
 * cap.
 */
export const MoveAccountGroupInputSchema = z.object({
    parentId: z.string().uuid().nullable(),
});
/**
 * Reorder a sibling set (all groups sharing `ledgerId` + `section` +
 * `parentId`) by supplying the desired ordering as an array of ids - intent,
 * not deltas. `orderedGroupIds` must be exactly the sibling set for the given
 * `(section, parentId)`; an id outside that set is rejected with 400.
 */
export const ReorderAccountGroupsInputSchema = z.object({
    section: LedgerAccountSectionSchema,
    parentId: z.string().uuid().nullable(),
    orderedGroupIds: z.array(z.string().uuid()).min(1).max(500),
});
/**
 * Set (or clear, via `groupId: null`) a single ledger account's group. The
 * target group must belong to the same ledger and the same section as the
 * account (service-enforced).
 */
export const SetAccountGroupInputSchema = z.object({
    groupId: z.string().uuid().nullable(),
});
/**
 * Response for `GET .../accounts/:ledgerAccountId/group`: the group a single
 * ledger account currently sits in, or `null` when it is ungrouped. Wrapped in
 * an object rather than returned as a bare nullable so the route keeps a
 * standard JSON-object response body, and so a later addition (the group's
 * ancestor path, say) is additive.
 */
export const LedgerAccountGroupResponseSchema = z.object({
    group: LedgerAccountGroupSchema.nullable(),
});
/**
 * Response envelope for `GET .../account-groups` (every group row for a
 * ledger - the structure-editing UI's flat read) and
 * `PUT .../account-groups/reorder` (one reordered sibling set). Not genuinely
 * paginated - `nextCursor`/`hasMore` are always `null`/`false` - but shares
 * the standard list-response envelope the other ledger list endpoints use
 * (e.g. `LedgerAccountListResponseSchema`).
 */
export const AccountGroupListResponseSchema = z.object({
    data: z.array(LedgerAccountGroupSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/**
 * Response for `DELETE .../account-groups/:groupId`: the deleted group's id
 * plus how many child groups and directly-grouped accounts were promoted to
 * its parent (see `account-group.service.ts` `deleteGroup`).
 */
export const DeleteAccountGroupResponseSchema = z.object({
    deletedGroupId: z.string(),
    promotedGroupCount: z.number().int().nonnegative(),
    promotedAccountCount: z.number().int().nonnegative(),
});
